import type { AnalyticsEvents } from './analytics';

export type PropType = 'string' | 'integer' | 'float' | 'boolean' | 'date' | 'list';

export type PropMap = { to: string; type: PropType; nullable?: boolean; values?: Record<string, string> };

// A CONSTANT property folded into the clean PostHog event — the "fold discriminator".
export type InjectMap = { value: string | number | boolean; type: PropType };

export type EventMapping = {
  event: string;
  properties?: Record<string, PropMap>;
  inject?: Record<string, InjectMap>;
  description?: string;
};

export const posthogEventMap: Record<AnalyticsEvents, EventMapping> = {
  add_child_button_clicked: { event: 'add_child_button_clicked' },
  free_first_lesson_started: { event: 'free_first_lesson_started' },
  quiz_started: {
    event: 'quiz_started',
    properties: {
      is_block_session: { to: 'is_block_session', type: 'boolean' },
      is_block_lesson: { to: 'is_block_lesson', type: 'boolean' },
      block_type: { to: 'block_type', type: 'string', nullable: true },
      lessonId: { to: 'lesson_id', type: 'string', nullable: true },
      quizId: { to: 'quiz_id', type: 'string' },
      quizRunId: { to: 'quiz_run_id', type: 'string' },
      quizType: { to: 'quiz_type', type: 'string' },
      cognition: { to: 'quiz_cognition', type: 'string', nullable: true },
      referrerType: { to: 'referrer_type', type: 'string', nullable: true },
      subject: { to: 'subject', type: 'string', nullable: true },
      step_part_index: { to: 'step_part_index', type: 'integer', nullable: true },
      step_parts_count: { to: 'step_parts_count', type: 'integer', nullable: true },
    },
  },
  quiz_finished: {
    event: 'quiz_finished',
    properties: {
      is_block_session: { to: 'is_block_session', type: 'boolean' },
      is_block_lesson: { to: 'is_block_lesson', type: 'boolean' },
      block_type: { to: 'block_type', type: 'string', nullable: true },
      lessonId: { to: 'lesson_id', type: 'string', nullable: true },
      quizType: { to: 'quiz_type', type: 'string' },
      cognition: { to: 'quiz_cognition', type: 'string', nullable: true },
      score: { to: 'score', type: 'float', nullable: true },
      questionsCorrect: { to: 'questions_correct_count', type: 'integer' },
      questionsAnswered: { to: 'questions_answered_count', type: 'integer' },
      quizId: { to: 'quiz_id', type: 'string' },
      quizRunId: { to: 'quiz_run_id', type: 'string' },
      referrerType: { to: 'referrer_type', type: 'string', nullable: true },
      isRecord: { to: 'is_record', type: 'boolean' },
      subject: { to: 'subject', type: 'string', nullable: true },
      step_part_index: { to: 'step_part_index', type: 'integer', nullable: true },
      step_parts_count: { to: 'step_parts_count', type: 'integer', nullable: true },
    },
  },
  quiz_answered: {
    event: 'quiz_answer_submitted',
    properties: {
      is_block_session: { to: 'is_block_session', type: 'boolean' },
      is_block_lesson: { to: 'is_block_lesson', type: 'boolean' },
      block_type: { to: 'block_type', type: 'string', nullable: true },
      lessonId: { to: 'lesson_id', type: 'string', nullable: true },
      questionId: { to: 'question_id', type: 'string' },
      validation_level: { to: 'validation_level', type: 'string', nullable: true },
      quizType: { to: 'quiz_type', type: 'string' },
      questionType: { to: 'question_type', type: 'string' },
      referrerType: { to: 'referrer_type', type: 'string', nullable: true },
      conversationId: { to: 'conversation_id', type: 'string' },
      input_method: { to: 'input_method', type: 'string' },
      subject: { to: 'subject', type: 'string', nullable: true },
      hearts_remaining: { to: 'hearts_remaining', type: 'integer', nullable: true },
    },
  },
  quiz_hearts_depleted: {
    event: 'quiz_hearts_depleted',
    properties: {
      lessonId: { to: 'lesson_id', type: 'string', nullable: true },
      quizId: { to: 'quiz_id', type: 'string' },
      quizType: { to: 'quiz_type', type: 'string' },
      subject: { to: 'subject', type: 'string', nullable: true },
    },
  },
  quiz_chapter_finished: {
    event: 'quiz_chapter_finished',
    properties: {
      lessonId: { to: 'lesson_id', type: 'string', nullable: true },
      quizType: { to: 'quiz_type', type: 'string' },
      conversationId: { to: 'conversation_id', type: 'string' },
      chapterUuid: { to: 'chapter_uuid', type: 'string' },
      chapter_id: { to: 'chapter_id', type: 'string', nullable: true },
      score: { to: 'score', type: 'float', nullable: true },
      subject: { to: 'subject', type: 'string', nullable: true },
    },
  },
  lesson_piece_created: {
    event: 'lesson_piece_created',
    properties: {
      lessonId: { to: 'lesson_id', type: 'string', nullable: true },
      lessonPieceId: { to: 'lesson_piece_id', type: 'string' },
      userType: { to: 'user_type', type: 'string' },
      subject: { to: 'subject', type: 'string', nullable: true },
    },
  },
  add_photo_cta_clicked: {
    event: 'photo_add_cta_clicked',
    properties: {
      subject: { to: 'subject', type: 'string', nullable: true },
      photosCount: { to: 'photos_count', type: 'integer' },
      userType: { to: 'user_type', type: 'string' },
    },
  },
  invite_user: {
    event: 'user_shared',
    properties: {
      from_user: { to: 'from_user', type: 'string' },
      to_user: { to: 'to_user', type: 'string' },
      family_code: { to: 'family_code', type: 'string' },
      activity_type: { to: 'activity_type', type: 'string' },
    },
  },
  signup: {
    event: 'account_signed_up',
    properties: {
      method: { to: 'auth_method', type: 'string', nullable: true },
      onesignal_wait_outcome: { to: 'onesignal_wait_outcome', type: 'string', nullable: true },
      onesignal_wait_ms: { to: 'onesignal_wait_ms', type: 'integer', nullable: true },
    },
  },
  signin: {
    event: 'account_signed_in',
    properties: {
      method: { to: 'auth_method', type: 'string', nullable: true },
    },
  },
  onboarding_complete: { event: 'onboarding_finished' },
  paywall_viewed: {
    event: 'paywall_viewed',
    properties: {
      type: { to: 'paywall_type', type: 'string' },
      placement: { to: 'placement', type: 'string' },
      offering_id: { to: 'offering_id', type: 'string', nullable: true },
      package_count: { to: 'package_count', type: 'integer' },
      package_types: { to: 'package_types', type: 'list', nullable: true },
      intro_price_package_types: { to: 'intro_price_package_types', type: 'list', nullable: true },
      product_ids: { to: 'product_ids', type: 'list', nullable: true },
      show_mensual_offer: { to: 'is_monthly_offer_shown', type: 'boolean' },
    },
  },
  trial_started: {
    event: 'trial_started',
    properties: {
      is_paid: { to: 'is_paid', type: 'boolean' },
      duration: { to: 'duration', type: 'string', nullable: true },
      package_type: { to: 'package_type', type: 'string' },
      source: { to: 'flow_source', type: 'string', nullable: true },
    },
  },
  trial_ended: { event: 'trial_finished' },
  support_opened: {
    event: 'support_opened',
    properties: {
      source: { to: 'support_source', type: 'string' },
    },
  },
  purchase: {
    event: 'paywall_purchase_finished',
    properties: {
      placement: { to: 'placement', type: 'string' },
      product_id: { to: 'product_id', type: 'string' },
      af_revenue: { to: 'revenue', type: 'float', nullable: true },
      af_currency: { to: 'currency', type: 'string', nullable: true },
    },
  },
  quit_quiz: {
    event: 'quiz_quit',
    properties: {
      is_block_session: { to: 'is_block_session', type: 'boolean' },
      is_block_lesson: { to: 'is_block_lesson', type: 'boolean' },
      block_type: { to: 'block_type', type: 'string', nullable: true },
      nbQuestions: { to: 'questions_count', type: 'integer' },
      referrerType: { to: 'referrer_type', type: 'string', nullable: true },
      quizType: { to: 'quiz_type', type: 'string' },
      cognition: { to: 'quiz_cognition', type: 'string', nullable: true },
      conversationId: { to: 'conversation_id', type: 'string' },
      chapterUuid: { to: 'chapter_uuid', type: 'string' },
      chapter_id: { to: 'chapter_id', type: 'string', nullable: true },
      lessonId: { to: 'lesson_id', type: 'string', nullable: true },
      subject: { to: 'subject', type: 'string', nullable: true },
    },
  },
  challenge_accepted: {
    event: 'challenge_selected',
    inject: {
      outcome: { value: 'accepted', type: 'string' },
    },
  },
  button_click: {
    event: 'cta_clicked',
    properties: {
      buttonName: { to: 'button_name', type: 'string', nullable: true },
      buttonTextContent: { to: 'button_text_content', type: 'string' },
    },
  },
  viral_invite_sent: { event: 'viral_invite_shared' },
  install_page_viewed: {
    event: 'install_page_viewed',
    properties: {
      has_referral_code: { to: 'has_referral_code', type: 'boolean' },
    },
    description: 'Web install page (web root) viewed by any visitor: desktops, organic traffic and invite links not sent to the store. Filter has_referral_code=true for shared invite links.',
  },
  referral_store_link_opened: {
    event: 'referral_store_link_opened',
    description: 'A phone opening a shared invite or referral link without the app was sent to the referral OneLink before the web app rendered.',
  },
  install_store_button_clicked: {
    event: 'install_store_button_clicked',
    properties: {
      store_badge: { to: 'store_badge', type: 'string' },
      has_referral_code: { to: 'has_referral_code', type: 'boolean' },
    },
    description: 'Store badge clicked on the web install page. store_badge=app_store|play_store is the badge tapped; the OneLink still routes by device.',
  },
  referral_code_sent: {
    event: 'referral_code_shared',
    properties: {
      share_method: { to: 'share_method', type: 'string' },
    },
    description: 'Referral code shared from the referral page (child or parent; user_type super-property tells them apart). share_method=native_share|clipboard.',
  },
  referral_button_clicked: {
    event: 'referral_button_clicked',
    properties: {
      display_mode: { to: 'display_mode', type: 'string' },
      has_badge: { to: 'has_badge', type: 'boolean' },
    },
    description: 'Referral entry button tapped in the parent header. display_mode=label|icon (icon under 390px), has_badge=the unseen dot was showing.',
  },
  referral_page_viewed: {
    event: 'referral_page_viewed',
    properties: {
      referees_count: { to: 'referees_count', type: 'integer', nullable: true },
    },
    description: 'Referral page opened (child or parent). Fired once the referees count resolves; null when the count request failed.',
  },
  referral_code_copy_clicked: {
    event: 'referral_code_copy_clicked',
    description: 'Copy button on the referral code tapped (child or parent referral page only).',
  },
  card_review_viewed: { event: 'card_review_viewed' },
  collectible_card_evolve_clicked: {
    event: 'collectible_card_evolve_clicked',
    properties: {
      card_type: { to: 'card_type', type: 'string' },
      card_rarity: { to: 'card_rarity', type: 'string' },
      owned_count: { to: 'owned_count', type: 'integer' },
    },
    description: 'The child tapped the evolve button on the card detail page: 5 cards of card_rarity merge into one of the next rarity and the evolution animation starts. owned_count is how many copies of that rarity the child held before the merge.',
  },
  collectible_card_evolution_failed: {
    event: 'collectible_card_evolution_failed',
    properties: {
      card_type: { to: 'card_type', type: 'string' },
      card_rarity: { to: 'card_rarity', type: 'string' },
    },
    description: 'The merge request behind a card evolution failed: the animation stops and an error toast shows. card_rarity is the starting rarity.',
  },
  collectible_card_rarity_selected: {
    event: 'collectible_card_rarity_selected',
    properties: {
      card_type: { to: 'card_type', type: 'string' },
      card_rarity: { to: 'card_rarity', type: 'string' },
      is_owned: { to: 'is_owned', type: 'boolean' },
      gesture: { to: 'gesture', type: 'string' },
    },
    description: 'The child moved the card detail carousel to another rarity of the same card. gesture is swipe or dot (position indicator tap); is_owned is false when that rarity is still missing from the collection.',
  },
  'star-review-0': {
    event: 'star_review_submitted',
    inject: { rating: { value: 0, type: 'integer' } },
    description: 'App-review star rating (0/5). The legacy 0-5 rating was encoded in the event name; the six events fold to one clean PostHog event and the rating is preserved as the constant `rating` property.',
  },
  'star-review-1': {
    event: 'star_review_submitted',
    inject: { rating: { value: 1, type: 'integer' } },
    description: 'App-review star rating (1/5). The legacy 0-5 rating was encoded in the event name; the six events fold to one clean PostHog event and the rating is preserved as the constant `rating` property.',
  },
  'star-review-2': {
    event: 'star_review_submitted',
    inject: { rating: { value: 2, type: 'integer' } },
    description: 'App-review star rating (2/5). The legacy 0-5 rating was encoded in the event name; the six events fold to one clean PostHog event and the rating is preserved as the constant `rating` property.',
  },
  'star-review-3': {
    event: 'star_review_submitted',
    inject: { rating: { value: 3, type: 'integer' } },
    description: 'App-review star rating (3/5). The legacy 0-5 rating was encoded in the event name; the six events fold to one clean PostHog event and the rating is preserved as the constant `rating` property.',
  },
  'star-review-4': {
    event: 'star_review_submitted',
    inject: { rating: { value: 4, type: 'integer' } },
    description: 'App-review star rating (4/5). The legacy 0-5 rating was encoded in the event name; the six events fold to one clean PostHog event and the rating is preserved as the constant `rating` property.',
  },
  'star-review-5': {
    event: 'star_review_submitted',
    inject: { rating: { value: 5, type: 'integer' } },
    description: 'App-review star rating (5/5). The legacy 0-5 rating was encoded in the event name; the six events fold to one clean PostHog event and the rating is preserved as the constant `rating` property.',
  },
  button_store_review_clicked: {
    event: 'button_store_review_clicked',
    properties: {
      userType: { to: 'user_type', type: 'string' },
      path: { to: 'path', type: 'string' },
      platform: { to: 'platform', type: 'string', nullable: true },
      trigger: { to: 'trigger', type: 'string' },
    },
  },
  store_review_prompt_requested: {
    event: 'store_review_prompt_requested',
    properties: {
      platform: { to: 'platform', type: 'string', nullable: true },
      trigger: { to: 'trigger', type: 'string' },
    },
    description:
      'The native in-app store review prompt was requested (Android at a 4-5 tap, both platforms in native mode). Whether the OS displayed it is unknowable.',
  },
  button_negative_review_clicked: { event: 'button_negative_review_clicked' },
  parent_challenged: {
    event: 'challenge_created',
    inject: {
      target: { value: 'parent', type: 'string' },
    },
  },
  parent_challenge_shared: {
    event: 'parent_challenge_shared',
    properties: {
      activity_type: { to: 'activity_type', type: 'string' },
    },
  },
  paywall_ambassador_code_validated: {
    event: 'paywall_ambassador_code_submitted',
    properties: {
      code: { to: 'code', type: 'string' },
    },
  },
  paywall_ambassador_code_valid: {
    event: 'paywall_ambassador_code_resolved',
    inject: { outcome: { value: 'valid', type: 'string' } },
    description: 'Server-validation result, kept DISTINCT from the submit, like referral_code_resolved. outcome=valid|invalid|error.',
  },
  paywall_ambassador_code_invalid: {
    event: 'paywall_ambassador_code_resolved',
    properties: {
      reason: { to: 'failure_reason', type: 'string' },
    },
    inject: { outcome: { value: 'invalid', type: 'string' } },
  },
  paywall_ambassador_code_error: {
    event: 'paywall_ambassador_code_resolved',
    inject: { outcome: { value: 'error', type: 'string' } },
    description: 'The request itself failed, so the code was never judged — distinct from a code the server refused.',
  },
  ambassador_trial_started: {
    event: 'ambassador_trial_started',
    properties: {
      ambassadorPartner: { to: 'ambassador_partner', type: 'string' },
      code: { to: 'code', type: 'string' },
    },
    description: 'A code from a named partner (Intermarché) opened its offered month. Marketing targets these families at the end of it; the partner is also written as a person property.',
  },
  lesson_subject_selected: {
    event: 'lesson_subject_selected',
    properties: {
      subject: { to: 'subject', type: 'string', nullable: true },
      child_id: { to: 'child_id', type: 'string' },
      userType: { to: 'user_type', type: 'string' },
    },
  },
  lesson_chapter_started: {
    event: 'lesson_chapter_started',
    properties: {
      is_block_session: { to: 'is_block_session', type: 'boolean' },
      is_block_lesson: { to: 'is_block_lesson', type: 'boolean' },
      block_type: { to: 'block_type', type: 'string', nullable: true },
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      chapter_id: { to: 'chapter_id', type: 'string', nullable: true },
      lesson_subject: { to: 'subject', type: 'string', nullable: true },
      chapter_kind: { to: 'chapter_kind', type: 'string', nullable: true },
    },
  },
  lesson_chapter_quit: {
    event: 'lesson_chapter_quit',
    properties: {
      is_block_session: { to: 'is_block_session', type: 'boolean' },
      is_block_lesson: { to: 'is_block_lesson', type: 'boolean' },
      block_type: { to: 'block_type', type: 'string', nullable: true },
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      chapter_id: { to: 'chapter_id', type: 'string', nullable: true },
      lesson_subject: { to: 'subject', type: 'string', nullable: true },
      chapter_kind: { to: 'chapter_kind', type: 'string', nullable: true },
      current_smartbit_index: { to: 'current_smartbit_index', type: 'integer' },
      total_smartbits: { to: 'smartbits_count', type: 'integer', nullable: true },
    },
  },
  lesson_chapter_finished: {
    event: 'lesson_chapter_finished',
    properties: {
      is_block_session: { to: 'is_block_session', type: 'boolean' },
      is_block_lesson: { to: 'is_block_lesson', type: 'boolean' },
      block_type: { to: 'block_type', type: 'string', nullable: true },
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      chapter_id: { to: 'chapter_id', type: 'string', nullable: true },
      lesson_subject: { to: 'subject', type: 'string', nullable: true },
      chapter_kind: { to: 'chapter_kind', type: 'string', nullable: true },
      smartbits_count: { to: 'smartbits_count', type: 'integer', nullable: true },
    },
  },
  smartbit_viewed: {
    event: 'smartbit_viewed',
    properties: {
      is_block_session: { to: 'is_block_session', type: 'boolean' },
      is_block_lesson: { to: 'is_block_lesson', type: 'boolean' },
      block_type: { to: 'block_type', type: 'string', nullable: true },
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      lesson_subject: { to: 'subject', type: 'string', nullable: true },
      chapter_kind: { to: 'chapter_kind', type: 'string', nullable: true },
      chapter_id: { to: 'chapter_id', type: 'string', nullable: true },
      smartbit_position: { to: 'smartbit_position', type: 'integer' },
      total_smartbits: { to: 'smartbits_count', type: 'integer', nullable: true },
    },
  },
  card_pack_opened: {
    event: 'card_pack_opened',
    properties: {
      cards_count: { to: 'cards_count', type: 'integer' },
      best_card_rarity: { to: 'best_card_rarity', type: 'string' },
    },
    description: 'The child tore the booster open on the third charge tap. best_card_rarity is the rarest card inside (common, rare, ultra_rare, secret).',
  },
  card_pack_card_revealed: {
    event: 'card_pack_card_viewed',
    properties: {
      card_type: { to: 'card_type', type: 'string' },
      card_rarity: { to: 'card_rarity', type: 'string' },
      card_index: { to: 'card_index', type: 'integer' },
      cards_count: { to: 'cards_count', type: 'integer' },
    },
    description: 'A card of the opened booster reached the top of the pile. card_index is its 0-based reveal order, weakest first.',
  },
  shared_lesson_landing_view: {
    event: 'shared_lesson_landing_viewed',
    properties: {
      lesson_uuid: { to: 'lesson_uuid', type: 'string' },
      source: { to: 'referral_source', type: 'string', nullable: true },
    },
  },
  shared_lesson_cta_click: {
    event: 'shared_lesson_cta_clicked',
    properties: {
      lesson_uuid: { to: 'lesson_uuid', type: 'string' },
      action: { to: 'cta_action', type: 'string', nullable: true },
    },
  },
  shared_lesson_import: {
    event: 'shared_lesson_created',
    properties: {
      lesson_uuid: { to: 'lesson_uuid', type: 'string' },
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      user_type: { to: 'user_type', type: 'string' },
      imported_for_child_id: { to: 'imported_for_child_id', type: 'string' },
    },
  },
  anonymous_locked_feature_click: {
    event: 'anonymous_locked_feature_clicked',
    properties: {
      feature_type: { to: 'feature_type', type: 'string' },
    },
  },
  anonymous_locked_feature_unlock_click: {
    event: 'anonymous_locked_feature_unlock_clicked',
    properties: {
      feature_type: { to: 'feature_type', type: 'string' },
    },
  },
  family_code_valid: {
    event: 'family_code_resolved',
    properties: {
      durationMs: { to: 'duration_ms', type: 'integer' },
    },
    inject: { outcome: { value: 'valid', type: 'string' } },
    description: 'Server verification of a family code typed on the family-code screen. outcome=valid|invalid|error; duration_ms is the verification round-trip.',
  },
  family_code_invalid: {
    event: 'family_code_resolved',
    properties: {
      durationMs: { to: 'duration_ms', type: 'integer' },
    },
    inject: { outcome: { value: 'invalid', type: 'string' } },
  },
  family_code_error: {
    event: 'family_code_resolved',
    properties: {
      durationMs: { to: 'duration_ms', type: 'integer' },
      errorType: { to: 'error_type', type: 'string' },
      httpStatus: { to: 'http_status', type: 'integer' },
    },
    inject: { outcome: { value: 'error', type: 'string' } },
    description: 'The verification request itself failed, so the code was never judged. error_type: network (no response) | timeout | http | unknown; http_status is 0 when no response arrived.',
  },
  share_family_code: {
    event: 'family_code_shared',
    properties: {
      family_code: { to: 'family_code', type: 'string' },
      activity_type: { to: 'activity_type', type: 'string' },
    },
  },
  lesson_created: {
    event: 'lesson_created',
    properties: {
      lessonId: { to: 'lesson_id', type: 'string', nullable: true },
      subject: { to: 'subject', type: 'string', nullable: true },
      evaluationDate: { to: 'evaluation_date', type: 'date', nullable: true },
      userType: { to: 'user_type', type: 'string' },
      child_id: { to: 'child_id', type: 'string' },
    },
  },
  lesson_evaluation_asked: {
    event: 'lesson_evaluation_submitted',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      answer: { to: 'answer', type: 'string' },
      userType: { to: 'user_type', type: 'string' },
    },
  },
  evaluation_date_entered: {
    event: 'evaluation_date_submitted',
    properties: {
      lessonId: { to: 'lesson_id', type: 'string', nullable: true },
      evaluationDate: { to: 'evaluation_date', type: 'date', nullable: true },
      subject: { to: 'subject', type: 'string', nullable: true },
    },
  },
  evaluation_date_skipped: {
    event: 'evaluation_date_submitted',
    properties: {
      lessonId: { to: 'lesson_id', type: 'string', nullable: true },
      subject: { to: 'subject', type: 'string', nullable: true },
      pageRoute: { to: 'page_route', type: 'string', nullable: true },
    },
    inject: {
      outcome: { value: 'skipped', type: 'string' },
    },
  },
  exam_subject_selected: {
    event: 'exam_subject_selected',
    properties: {
      subject: { to: 'subject', type: 'string', nullable: true },
      child_id: { to: 'child_id', type: 'string' },
      userType: { to: 'user_type', type: 'string' },
    },
  },
  exam_created: {
    event: 'exam_created',
    properties: {
      examId: { to: 'exam_id', type: 'string' },
      subject: { to: 'subject', type: 'string', nullable: true },
    },
  },
  exam_analysis_completed: {
    event: 'exam_analysis_finished',
    properties: {
      examId: { to: 'exam_id', type: 'string' },
      subject: { to: 'subject', type: 'string', nullable: true },
    },
  },
  error_toast_shown: {
    event: 'error_toast_viewed',
    properties: {
      errorMessageId: { to: 'error_message_id', type: 'string', nullable: true },
      errorType: { to: 'error_type', type: 'string' },
      httpStatus: { to: 'http_status', type: 'integer' },
      apiRoute: { to: 'api_route', type: 'string' },
      pagePath: { to: 'page_path', type: 'string' },
      pageRoute: { to: 'page_route', type: 'string', nullable: true },
    },
  },
  onboarding_three_month_goals_viewed: {
    event: 'onboarding_three_month_goals_viewed',
    description: 'The parent onboarding "In 3 months" goals step was shown (A/B test arm).',
    properties: {
      control_feat_variant: { to: 'control_feat_variant', type: 'string', nullable: true },
    },
  },
  onboarding_three_month_goals_submitted: {
    event: 'onboarding_three_month_goals_submitted',
    description: 'The parent picked their goals for their child in 3 months and continued the onboarding.',
    properties: {
      control_feat_variant: { to: 'control_feat_variant', type: 'string', nullable: true },
      goals: { to: 'three_month_goals', type: 'list' },
    },
  },
  onboarding_labor_effect_viewed: {
    event: 'onboarding_labor_effect_viewed',
    description: 'The parent onboarding "preparing your child\'s space" screen was shown, with its progress bar running (A/B test arm).',
    properties: {
      control_feat_variant: { to: 'control_feat_variant', type: 'string', nullable: true },
    },
  },
  onboarding_labor_effect_space_opened: {
    event: 'onboarding_labor_effect_space_opened',
    description: 'The parent tapped "See their space" once the preparation finished, towards the recap screen (see onboarding_labor_effect_ready_viewed).',
    properties: {
      control_feat_variant: { to: 'control_feat_variant', type: 'string', nullable: true },
    },
  },
  onboarding_labor_effect_ready_viewed: {
    event: 'onboarding_labor_effect_ready_viewed',
    description: 'The parent onboarding recap of the child\'s class, goals and Baobab actions was shown, including when coming back to it.',
    properties: {
      control_feat_variant: { to: 'control_feat_variant', type: 'string', nullable: true },
    },
  },
  onboarding_labor_effect_finished: {
    event: 'onboarding_labor_effect_finished',
    description: 'The parent left the onboarding space recap with "Continue", towards the notification step.',
    properties: {
      control_feat_variant: { to: 'control_feat_variant', type: 'string', nullable: true },
    },
  },
  push_prompt_viewed: {
    event: 'push_prompt_viewed',
    description: 'A push-notification pre-prompt (the in-app screen shown before the native permission dialog) was shown; placement says where.',
    properties: {
      placement: { to: 'placement', type: 'string' },
      userType: { to: 'user_type', type: 'string' },
    },
  },
  push_permission_result: {
    event: 'push_permission_resolved',
    properties: {
      status: { to: 'permission_status', type: 'string' },
      userType: { to: 'user_type', type: 'string' },
      placement: { to: 'placement', type: 'string' },
    },
  },
  att_permission_result: {
    event: 'att_permission_resolved',
    properties: {
      status: { to: 'permission_status', type: 'string' },
    },
  },
  shop_floating_button_clicked: { event: 'shop_floating_button_clicked' },
  cards_empty_state_viewed: {
    event: 'cards_empty_state_viewed',
    description: 'The child opened their card collection while owning no card, so the buy-a-booster empty state showed. Fires once per visit, whichever view is shown.',
  },
  cards_empty_state_shop_clicked: {
    event: 'cards_empty_state_shop_clicked',
    description: 'The child tapped "Buy a booster" on the empty collection state and went to the Baoutique.',
  },
  reading_aid_enabled: {
    event: 'reading_aid_updated',
    inject: {
      is_enabled: { value: true, type: 'boolean' },
    },
  },
  vsaq_hint_requested: {
    event: 'vsaq_hint_clicked',
    properties: {
      quizType: { to: 'quiz_type', type: 'string' },
      hintNumber: { to: 'hint_number', type: 'integer' },
      questionId: { to: 'question_id', type: 'string' },
      conversationId: { to: 'conversation_id', type: 'string' },
      lessonId: { to: 'lesson_id', type: 'string', nullable: true },
      subject: { to: 'subject', type: 'string', nullable: true },
    },
  },
  onboarding_role_selected: {
    event: 'onboarding_role_selected',
    properties: {
      role: { to: 'user_role', type: 'string' },
    },
  },
  'onboarding_free-trial': { event: 'onboarding_free_trial_viewed' },
  onboarding_reminder: { event: 'onboarding_reminder_viewed' },
  paywall_error: {
    event: 'paywall_error_viewed',
    properties: {
      platform: { to: 'platform', type: 'string', nullable: true },
    },
  },
  paywall_restore_finished: {
    event: 'paywall_restore_finished',
    properties: {
      placement: { to: 'placement', type: 'string' },
      has_entitlement: { to: 'has_entitlement', type: 'boolean' },
    },
    description: 'A restore from the native paywall finished; has_entitlement tells whether a subscription was actually restored.',
  },
  paywall_result_received: {
    event: 'paywall_result_finished',
    properties: {
      placement: { to: 'placement', type: 'string' },
      result: { to: 'paywall_result', type: 'string' },
      purchase_completed: { to: 'is_purchase_completed', type: 'boolean' },
    },
    description:
      'Native paywall presentation ended: the raw SDK result and whether onPurchaseCompleted fired, to expose cancelled-after-purchase.',
  },
  revenuecat_identity_resolved: {
    event: 'revenuecat_identity_resolved',
    description:
      'RevenueCat identity resolved after a logIn attempt: noop (already this family), identified (from an anonymous id), switched (from another family) or failed. Emitted on native only.',
  },
  funnel_return_page_viewed: {
    event: 'funnel_return_page_viewed',
    properties: {
      session_id: { to: 'session_id', type: 'string' },
    },
  },
  funnel_return_code_displayed: {
    event: 'funnel_return_code_viewed',
    properties: {
      session_id: { to: 'session_id', type: 'string' },
    },
  },
  funnel_return_download_clicked: {
    event: 'funnel_return_download_clicked',
    properties: {
      session_id: { to: 'session_id', type: 'string' },
      platform: { to: 'platform', type: 'string', nullable: true },
    },
  },
  funnel_download_app_viewed: { event: 'funnel_download_app_viewed' },
  funnel_download_app_clicked: {
    event: 'funnel_download_app_clicked',
    properties: {
      platform: { to: 'platform', type: 'string', nullable: true },
    },
  },
  funnel_web_paywall_purchase_started: { event: 'funnel_web_paywall_purchase_started' },
  funnel_web_paywall_purchase_completed: { event: 'funnel_web_paywall_purchase_finished' },
  funnel_landing_viewed: {
    event: 'funnel_landing_viewed',
    properties: {
      funnel_country: { to: 'funnel_country', type: 'string' },
      angle: { to: 'angle', type: 'string' },
    },
    description: 'Web funnel landing page rendered (page 1), per funnel country and angle.',
  },
  funnel_landing_cta_clicked: {
    event: 'funnel_landing_cta_clicked',
    properties: {
      funnel_country: { to: 'funnel_country', type: 'string' },
      angle: { to: 'angle', type: 'string' },
      cta_position: { to: 'cta_position', type: 'string' },
    },
    description: 'Web funnel landing CTA clicked; cta_position tells the four buttons apart.',
  },
  funnel_consent_submitted: {
    event: 'funnel_consent_submitted',
    properties: {
      status: { to: 'consent_status', type: 'string' },
    },
    description: 'Cookie consent choice on the web funnel of a consent country (FR).',
  },
  missions_page_viewed: {
    event: 'missions_page_viewed',
    properties: {
      daily_count: { to: 'daily_count', type: 'integer' },
      weekly_count: { to: 'weekly_count', type: 'integer' },
      available_count: { to: 'available_count', type: 'integer' },
    },
  },
  mission_reward_available_shown: {
    event: 'mission_reward_available_viewed',
    properties: {
      template_code: { to: 'template_code', type: 'string' },
      frequency: { to: 'frequency', type: 'string' },
      is_meta: { to: 'is_meta', type: 'boolean' },
      reward_type: { to: 'reward_type', type: 'string' },
      reward_amount: { to: 'reward_amount', type: 'integer' },
    },
  },
  mission_reward_claimed: {
    event: 'mission_reward_claim_clicked',
    properties: {
      template_code: { to: 'template_code', type: 'string' },
      reward_type: { to: 'reward_type', type: 'string' },
      reward_amount: { to: 'reward_amount', type: 'integer' },
      frequency: { to: 'frequency', type: 'string' },
    },
  },
  missions_claim_all_opened: {
    event: 'missions_claim_all_screen_viewed',
    properties: {
      frequency: { to: 'frequency', type: 'string' },
      shown_count: { to: 'shown_count', type: 'integer' },
    },
    description: "Collect screen opened from the missions page 'Tout collecter' CTA; shown_count is how many rewards the child was offered.",
  },
  missions_claim_all_confirmed: {
    event: 'missions_all_rewards_claimed',
    properties: {
      frequency: { to: 'frequency', type: 'string' },
      claim_count: { to: 'claim_count', type: 'integer' },
      skipped_count: { to: 'skipped_count', type: 'integer' },
    },
    description: 'Server paid the bulk claim. claim_count is what was actually granted and skipped_count what it could not hand over, so the pair never has to be read against the count the screen showed.',
  },
  referral_code_screen_viewed: { event: 'referral_code_screen_viewed' },
  referral_code_submitted: {
    event: 'referral_code_submitted',
    properties: {
      code: { to: 'code', type: 'string' },
    },
  },
  referral_code_valid: {
    event: 'referral_code_resolved',
    inject: { outcome: { value: 'valid', type: 'string' } },
    description: 'Async server-validation result (fires after referral_code_submitted resolves), so it is kept DISTINCT from the submit — not merged into referral_code_submitted. outcome=valid|invalid|skipped.',
  },
  referral_code_invalid: {
    event: 'referral_code_resolved',
    inject: { outcome: { value: 'invalid', type: 'string' } },
  },
  referral_code_skipped: {
    event: 'referral_code_resolved',
    inject: { outcome: { value: 'skipped', type: 'string' } },
  },
  multi_photo_page_viewed: {
    event: 'photo_page_viewed',
    properties: {
      subject: { to: 'subject', type: 'string', nullable: true },
      userType: { to: 'user_type', type: 'string' },
    },
    inject: {
      photo_context: { value: 'lesson', type: 'string' },
    },
  },
  multi_photo_source_panel_opened: {
    event: 'photo_source_panel_opened',
    properties: {
      subject: { to: 'subject', type: 'string', nullable: true },
      is_retake: { to: 'is_retake', type: 'boolean' },
      userType: { to: 'user_type', type: 'string' },
    },
    inject: {
      photo_context: { value: 'lesson', type: 'string' },
    },
  },
  multi_photo_source_selected: {
    event: 'photo_source_selected',
    properties: {
      subject: { to: 'subject', type: 'string', nullable: true },
      source: { to: 'photo_source', type: 'string', nullable: true },
      is_retake: { to: 'is_retake', type: 'boolean' },
      photos_count: { to: 'photos_count', type: 'integer' },
      userType: { to: 'user_type', type: 'string' },
    },
    inject: {
      photo_context: { value: 'lesson', type: 'string' },
    },
  },
  multi_photo_permission_denied: {
    event: 'photo_permission_resolved',
    properties: {
      subject: { to: 'subject', type: 'string', nullable: true },
      permission: { to: 'permission', type: 'string' },
      userType: { to: 'user_type', type: 'string' },
    },
    inject: {
      photo_context: { value: 'lesson', type: 'string' },
      permission_status: { value: 'denied', type: 'string' },
    },
  },
  multi_photo_upload_failed: {
    event: 'photo_upload_failed',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      error: { to: 'error', type: 'string', nullable: true },
      userType: { to: 'user_type', type: 'string' },
    },
    inject: {
      photo_context: { value: 'lesson', type: 'string' },
    },
  },
  multi_photo_upload_retry: {
    event: 'photo_upload_clicked',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      userType: { to: 'user_type', type: 'string' },
    },
    inject: {
      photo_context: { value: 'lesson', type: 'string' },
      is_retry: { value: true, type: 'boolean' },
    },
  },
  multi_photo_removed: {
    event: 'photo_deleted',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      photo_status: { to: 'photo_status', type: 'string' },
      userType: { to: 'user_type', type: 'string' },
    },
    inject: {
      photo_context: { value: 'lesson', type: 'string' },
    },
  },
  multi_photo_edit_opened: {
    event: 'photo_edit_opened',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      userType: { to: 'user_type', type: 'string' },
    },
    inject: {
      photo_context: { value: 'lesson', type: 'string' },
    },
  },
  multi_photo_edit_saved: {
    event: 'photo_edit_updated',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      rotation: { to: 'rotation', type: 'integer' },
      had_crop: { to: 'has_crop', type: 'boolean' },
      userType: { to: 'user_type', type: 'string' },
    },
    inject: {
      photo_context: { value: 'lesson', type: 'string' },
    },
  },
  multi_photo_preprocess_error_shown: {
    event: 'photo_preprocess_error_viewed',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      userType: { to: 'user_type', type: 'string' },
      failedCriteria: { to: 'failed_criteria', type: 'list' },
      severity: { to: 'photo_check_severity', type: 'string' },
      trigger: { to: 'photo_check_trigger', type: 'string' },
    },
    inject: {
      photo_context: { value: 'lesson', type: 'string' },
    },
  },
  multi_photo_preprocess_error_action: {
    event: 'photo_preprocess_error_action_clicked',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      action: { to: 'error_action', type: 'string', nullable: true },
      userType: { to: 'user_type', type: 'string' },
      failedCriteria: { to: 'failed_criteria', type: 'list' },
      severity: { to: 'photo_check_severity', type: 'string' },
    },
    inject: {
      photo_context: { value: 'lesson', type: 'string' },
    },
  },
  multi_photo_tips_opened: {
    event: 'photo_tips_opened',
    properties: {
      subject: { to: 'subject', type: 'string', nullable: true },
      userType: { to: 'user_type', type: 'string' },
    },
    inject: {
      photo_context: { value: 'lesson', type: 'string' },
    },
  },
  lesson_generation_error_viewed: {
    event: 'lesson_generation_error_viewed',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      lesson_kind: { to: 'lesson_kind', type: 'string' },
    },
    description: 'Lesson page shows the panel saying part of the lesson could not be generated.',
  },
  lesson_generation_error_closed: {
    event: 'lesson_generation_error_closed',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      lesson_kind: { to: 'lesson_kind', type: 'string' },
    },
    description: 'Child closes the generation-error panel without relaunching.',
  },
  lesson_generation_retry_clicked: {
    event: 'lesson_generation_retry_clicked',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      lesson_kind: { to: 'lesson_kind', type: 'string' },
    },
    description: 'Child relaunches the lesson generation from the generation-error panel.',
  },
  multi_photo_validate_clicked: {
    event: 'photo_validate_clicked',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      photos_count: { to: 'photos_count', type: 'integer' },
      userType: { to: 'user_type', type: 'string' },
      lesson_kind: { to: 'lesson_kind', type: 'string' },
      control_feat_variant: { to: 'control_feat_variant', type: 'string', nullable: true },
      control_feat_resolved: { to: 'control_feat_resolved', type: 'boolean' },
    },
    inject: {
      photo_context: { value: 'lesson', type: 'string' },
    },
  },
  multi_photo_cancelled: {
    event: 'photo_quit',
    properties: {
      subject: { to: 'subject', type: 'string', nullable: true },
      photos_count: { to: 'photos_count', type: 'integer' },
      userType: { to: 'user_type', type: 'string' },
    },
    inject: {
      photo_context: { value: 'lesson', type: 'string' },
    },
  },
  multi_exam_photo_page_viewed: {
    event: 'photo_page_viewed',
    properties: {
      subject: { to: 'subject', type: 'string', nullable: true },
      tone: { to: 'tone', type: 'string' },
    },
    inject: {
      photo_context: { value: 'exam', type: 'string' },
    },
  },
  multi_exam_photo_source_panel_opened: {
    event: 'photo_source_panel_opened',
    properties: {
      subject: { to: 'subject', type: 'string', nullable: true },
      is_retake: { to: 'is_retake', type: 'boolean' },
    },
    inject: {
      photo_context: { value: 'exam', type: 'string' },
    },
  },
  multi_exam_photo_source_selected: {
    event: 'photo_source_selected',
    properties: {
      subject: { to: 'subject', type: 'string', nullable: true },
      source: { to: 'photo_source', type: 'string', nullable: true },
      is_retake: { to: 'is_retake', type: 'boolean' },
      photos_count: { to: 'photos_count', type: 'integer' },
    },
    inject: {
      photo_context: { value: 'exam', type: 'string' },
    },
  },
  multi_exam_photo_permission_denied: {
    event: 'photo_permission_resolved',
    properties: {
      subject: { to: 'subject', type: 'string', nullable: true },
      permission: { to: 'permission', type: 'string' },
    },
    inject: {
      photo_context: { value: 'exam', type: 'string' },
      permission_status: { value: 'denied', type: 'string' },
    },
  },
  multi_exam_photo_upload_failed: {
    event: 'photo_upload_failed',
    properties: {
      exam_id: { to: 'exam_id', type: 'string' },
      error: { to: 'error', type: 'string', nullable: true },
    },
    inject: {
      photo_context: { value: 'exam', type: 'string' },
    },
  },
  multi_exam_photo_upload_retry: {
    event: 'photo_upload_clicked',
    properties: {
      exam_id: { to: 'exam_id', type: 'string' },
    },
    inject: {
      photo_context: { value: 'exam', type: 'string' },
      is_retry: { value: true, type: 'boolean' },
    },
  },
  multi_exam_photo_removed: {
    event: 'photo_deleted',
    properties: {
      exam_id: { to: 'exam_id', type: 'string' },
      photo_status: { to: 'photo_status', type: 'string' },
    },
    inject: {
      photo_context: { value: 'exam', type: 'string' },
    },
  },
  multi_exam_photo_edit_opened: {
    event: 'photo_edit_opened',
    properties: {
      exam_id: { to: 'exam_id', type: 'string' },
    },
    inject: {
      photo_context: { value: 'exam', type: 'string' },
    },
  },
  multi_exam_photo_edit_saved: {
    event: 'photo_edit_updated',
    properties: {
      exam_id: { to: 'exam_id', type: 'string' },
      rotation: { to: 'rotation', type: 'integer' },
      had_crop: { to: 'has_crop', type: 'boolean' },
    },
    inject: {
      photo_context: { value: 'exam', type: 'string' },
    },
  },
  multi_exam_photo_preprocess_error_shown: {
    event: 'photo_preprocess_error_viewed',
    properties: {
      exam_id: { to: 'exam_id', type: 'string' },
      failedCriteria: { to: 'failed_criteria', type: 'list' },
      severity: { to: 'photo_check_severity', type: 'string' },
      trigger: { to: 'photo_check_trigger', type: 'string' },
    },
    inject: {
      photo_context: { value: 'exam', type: 'string' },
    },
  },
  multi_exam_photo_preprocess_error_action: {
    event: 'photo_preprocess_error_action_clicked',
    properties: {
      exam_id: { to: 'exam_id', type: 'string' },
      action: { to: 'error_action', type: 'string', nullable: true },
      failedCriteria: { to: 'failed_criteria', type: 'list' },
      severity: { to: 'photo_check_severity', type: 'string' },
    },
    inject: {
      photo_context: { value: 'exam', type: 'string' },
    },
  },
  multi_exam_photo_tips_opened: {
    event: 'photo_tips_opened',
    properties: {
      subject: { to: 'subject', type: 'string', nullable: true },
      tone: { to: 'tone', type: 'string' },
    },
    inject: {
      photo_context: { value: 'exam', type: 'string' },
    },
  },
  multi_exam_photo_validate_clicked: {
    event: 'photo_validate_clicked',
    properties: {
      exam_id: { to: 'exam_id', type: 'string' },
      photos_count: { to: 'photos_count', type: 'integer' },
    },
    inject: {
      photo_context: { value: 'exam', type: 'string' },
    },
  },
  multi_exam_photo_cancelled: {
    event: 'photo_quit',
    properties: {
      subject: { to: 'subject', type: 'string', nullable: true },
      photos_count: { to: 'photos_count', type: 'integer' },
    },
    inject: {
      photo_context: { value: 'exam', type: 'string' },
    },
  },
  exam_piece_created: {
    event: 'exam_piece_created',
    properties: {
      examId: { to: 'exam_id', type: 'string' },
      examPieceId: { to: 'exam_piece_id', type: 'string' },
      subject: { to: 'subject', type: 'string', nullable: true },
    },
  },
  lesson_photos_gallery_viewed: {
    event: 'photo_gallery_viewed',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      photos_count: { to: 'photos_count', type: 'integer' },
    },
    inject: {
      photo_context: { value: 'lesson', type: 'string' },
    },
  },
  lesson_photos_edit_confirmed: {
    event: 'photo_gallery_edit_submitted',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
    },
    inject: {
      photo_context: { value: 'lesson', type: 'string' },
    },
  },
  lesson_photo_zoom_opened: {
    event: 'photo_zoom_opened',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
    },
    inject: {
      photo_context: { value: 'lesson', type: 'string' },
    },
  },
  lesson_flow_step_viewed: {
    event: 'lesson_flow_step_viewed',
    properties: {
      step_kind: { to: 'step_kind', type: 'string' },
      step_index: { to: 'step_index', type: 'integer' },
    },
  },
  lesson_flow_continue_clicked: {
    event: 'lesson_flow_continue_clicked',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      userType: { to: 'user_type', type: 'string' },
    },
  },
  lesson_resource_loading_finished: {
    event: 'lesson_resource_loading_finished',
    properties: {
      lessonId: { to: 'lesson_id', type: 'string', nullable: true },
      subject: { to: 'subject', type: 'string', nullable: true },
      resourceKind: { to: 'resource_kind', type: 'string' },
      resourceOrder: { to: 'resource_order', type: 'integer' },
      msSinceLessonCreated: { to: 'ms_since_lesson_created', type: 'integer' },
      photosCount: { to: 'photos_count', type: 'integer' },
    },
  },
  parent_add_lesson_clicked: {
    event: 'parent_add_lesson_clicked',
    properties: {
      children_count: { to: 'children_count', type: 'integer' },
    },
  },
  parent_add_lesson_child_selected: {
    event: 'parent_add_lesson_child_selected',
    properties: {
      child_id: { to: 'child_id', type: 'string' },
    },
  },
  parent_add_lesson_created_modal_cta_clicked: {
    event: 'parent_add_lesson_created_modal_cta_clicked',
    properties: {
      cta: { to: 'cta', type: 'string' },
    },
  },
  parent_lesson_viewed: {
    event: 'parent_lesson_viewed',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      is_lesson_started: { to: 'is_lesson_started', type: 'boolean' },
      progression_percent: { to: 'progression_percent', type: 'integer' },
    },
    description: "Parent opened a child's lesson page, with the child's progression on it.",
  },
  parent_lesson_tab_selected: {
    event: 'parent_lesson_tab_selected',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      parent_lesson_tab: { to: 'parent_lesson_tab', type: 'string' },
      is_from_empty_state: { to: 'is_from_empty_state', type: 'boolean' },
    },
    description: "Parent switched the child's lesson page between its progression and its revision sheets.",
  },
  parent_lesson_knowledge_component_clicked: {
    event: 'parent_lesson_knowledge_component_clicked',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      knowledge_component_id: { to: 'knowledge_component_id', type: 'string' },
      kc_mastery: { to: 'kc_mastery', type: 'string', nullable: true },
    },
    description: "Parent opened a knowledge component's explanation on the child's lesson page.",
  },
  parent_lesson_knowledge_chapter_clicked: {
    event: 'parent_lesson_knowledge_chapter_clicked',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      chapter_id: { to: 'chapter_id', type: 'string', nullable: true },
      is_chapter_expanded: { to: 'is_chapter_expanded', type: 'boolean' },
    },
    description: "Parent folded or unfolded a chapter of the child's knowledge on the lesson page.",
  },
  parent_lesson_mastery_status_clicked: {
    event: 'parent_lesson_mastery_status_clicked',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      kc_mastery: { to: 'kc_mastery', type: 'string', nullable: true },
      is_chapter_status: { to: 'is_chapter_status', type: 'boolean' },
    },
    description: "Parent tapped a mastery tag on the child's lesson page to read what each status means.",
  },
  parent_lesson_revision_sheet_clicked: {
    event: 'parent_lesson_revision_sheet_clicked',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      chapter_id: { to: 'chapter_id', type: 'string', nullable: true },
      chapter_kind: { to: 'chapter_kind', type: 'string', nullable: true },
    },
    description: "Parent opened a revision sheet from the child's lesson page.",
  },
  timetable_upload_started: {
    event: 'timetable_upload_started',
    properties: {
      timetable_source: { to: 'timetable_source', type: 'string' },
      userType: { to: 'user_type', type: 'string' },
      timetable_child_count: { to: 'timetable_child_count', type: 'integer', nullable: true },
    },
    description: 'The timetable page opened (US-1368). Source: iam, settings, email, push or deeplink.',
  },
  timetable_photo_created: {
    event: 'timetable_photo_created',
    properties: {
      timetable_source: { to: 'timetable_source', type: 'string' },
      timetable_photo_source: { to: 'timetable_photo_source', type: 'string' },
      timetable_file_type: { to: 'timetable_file_type', type: 'string' },
    },
    description: 'One timetable photo was stored on the backend (POST /api/timetables/pieces succeeded).',
  },
  timetable_photo_deleted: {
    event: 'timetable_photo_deleted',
    properties: {
      timetable_source: { to: 'timetable_source', type: 'string' },
      timetable_file_type: { to: 'timetable_file_type', type: 'string' },
    },
    description: 'One timetable photo was removed (DELETE succeeded).',
  },
  query_error_retry_clicked: {
    event: 'query_error_retry_clicked',
    properties: {
      errorScreen: { to: 'error_screen', type: 'string' },
    },
    description: 'The retry button of a full-page load error state was tapped. error_screen names the page whose request had failed.',
  },
  timetable_photo_retry_clicked: {
    event: 'timetable_photo_retry_clicked',
    properties: {
      timetable_source: { to: 'timetable_source', type: 'string' },
      timetable_file_type: { to: 'timetable_file_type', type: 'string' },
    },
    description: 'A timetable photo tile in upload_error status was tapped to retry the upload.',
  },
  timetable_photo_viewed: {
    event: 'timetable_photo_viewed',
    properties: {
      timetable_source: { to: 'timetable_source', type: 'string' },
      timetable_file_type: { to: 'timetable_file_type', type: 'string' },
    },
    description: 'A stored timetable photo tile was tapped and opened in the full-screen preview.',
  },
  timetable_submitted: {
    event: 'timetable_submitted',
    properties: {
      timetable_source: { to: 'timetable_source', type: 'string' },
      timetable_photo_count: { to: 'timetable_photo_count', type: 'integer' },
      userType: { to: 'user_type', type: 'string' },
    },
    description: 'Valider tapped and POST /confirm succeeded. The backend emits the same event server-side.',
  },
  timetable_settings_viewed: {
    event: 'timetable_settings_viewed',
    properties: {
      userType: { to: 'user_type', type: 'string' },
    },
    description: 'The timetable page opened from the child profile row (source=settings).',
  },
  timetable_updated: {
    event: 'timetable_updated',
    properties: {
      timetable_photo_count: { to: 'timetable_photo_count', type: 'integer' },
    },
    description: 'A photo was added or removed from the settings origin.',
  },
  timetable_picker_later_clicked: {
    event: 'timetable_picker_later_clicked',
    properties: {
      timetable_source: { to: 'timetable_source', type: 'string' },
      timetable_child_count: { to: 'timetable_child_count', type: 'integer', nullable: true },
    },
    description: 'Later tapped on the parent child-timetable picker, before navigating away.',
  },
  subjects_updated: {
    event: 'subjects_updated',
    properties: {
      subjects: { to: 'subjects', type: 'list' },
      subjects_count: { to: 'subjects_count', type: 'integer' },
      source: { to: 'change_source', type: 'string', nullable: true },
      child_id: { to: 'child_id', type: 'string' },
      is_self: { to: 'is_self', type: 'boolean' },
    },
  },
  baohelp_opened: {
    event: 'baohelp_opened',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      entry_point: { to: 'entry_point', type: 'string' },
      control_feat_variant: { to: 'control_feat_variant', type: 'string', nullable: true },
      chapter_kind: { to: 'chapter_kind', type: 'string', nullable: true },
      step_id: { to: 'step_id', type: 'string', nullable: true },
      practice_kind: { to: 'practice_kind', type: 'string' },
      practice_part: { to: 'practice_part', type: 'string' },
      step_index: { to: 'step_index', type: 'integer' },
      is_resumed: { to: 'is_resumed', type: 'boolean', nullable: true },
      history_bubble_count: { to: 'history_bubble_count', type: 'integer', nullable: true },
      error_kind: { to: 'error_kind', type: 'string', nullable: true },
    },
    description: 'Bao tutor session started: sheet open on the lesson entry points (smartbit, chapter_end, quiz_wrong, highlight for a term tapped in a block passage, which was smartbit before, and practice for a Practice step reading screen or one of its terms), first focus of the bar on entry_point home (so the home arm counts taps on the bar, not sheet opens — do not compare its open-to-message funnel with the lesson arms). On the lesson entry points is_resumed is true when the sheet reopens on the exchange left on the same anchor (an engine conversation, even since closed by the engine, or its first reply still pending) (quiz run attempt, question and answer; smartbit; passage term; chapter end) earlier in the app session, and history_bubble_count is how many bubbles the thread already shows besides the greeting (from the child and from Bao, error bubbles included; can be > 0 with is_resumed false after a failed start); both are absent on entry_point home. On entry_point history (a past conversation reopened from the history, US-1390) it fires once the conversation is loaded, is_resumed is always true and history_bubble_count is how many bubbles the reopened conversation shows (absent when it fails to load, where error_kind is closed for a conversation gone on the engine (404, 409, 410), transient otherwise); filter it out of the per-entry-point and home-tutor experiment funnels. entry_point history_new is a home-bar session started right after the new conversation button of the history. chapter_kind (lesson_chapter) is set only when the tutor opens from a Revision sheet card, which tells a sheet chat from a block chat.',
  },
  baohelp_hook_sent: {
    event: 'baohelp_hook_submitted',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      entry_point: { to: 'entry_point', type: 'string' },
      control_feat_variant: { to: 'control_feat_variant', type: 'string', nullable: true },
      chapter_kind: { to: 'chapter_kind', type: 'string', nullable: true },
      step_id: { to: 'step_id', type: 'string', nullable: true },
      practice_kind: { to: 'practice_kind', type: 'string' },
      practice_part: { to: 'practice_part', type: 'string' },
      step_index: { to: 'step_index', type: 'integer' },
      hook: { to: 'hook', type: 'string' },
    },
  },
  baohelp_suggestion_sent: {
    event: 'baohelp_suggestion_submitted',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      entry_point: { to: 'entry_point', type: 'string' },
      control_feat_variant: { to: 'control_feat_variant', type: 'string', nullable: true },
      chapter_kind: { to: 'chapter_kind', type: 'string', nullable: true },
      step_id: { to: 'step_id', type: 'string', nullable: true },
      practice_kind: { to: 'practice_kind', type: 'string' },
      practice_part: { to: 'practice_part', type: 'string' },
      step_index: { to: 'step_index', type: 'integer' },
      label: { to: 'suggestion_label', type: 'string' },
      position: { to: 'position', type: 'integer' },
      suggestion_kind: { to: 'suggestion_kind', type: 'string' },
    },
    description: "A chip Bao proposed at the end of its reply was tapped: suggestion_label is the chip text and position its zero-based rank in the reply's list. suggestion_kind is what the chip points at — text (a plain follow-up), subject, lesson or lesson_part — derived client-side from the chip's ref; on the lesson sheet a chip is text, or lesson_part on a Lesson-profile conversation (a part of the anchored lesson; never subject or lesson). The opening choices of the first turn stay on baohelp_hook_submitted.",
  },
  baohelp_message_sent: {
    event: 'baohelp_message_submitted',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      entry_point: { to: 'entry_point', type: 'string' },
      control_feat_variant: { to: 'control_feat_variant', type: 'string', nullable: true },
      chapter_kind: { to: 'chapter_kind', type: 'string', nullable: true },
      step_id: { to: 'step_id', type: 'string', nullable: true },
      practice_kind: { to: 'practice_kind', type: 'string' },
      practice_part: { to: 'practice_part', type: 'string' },
      step_index: { to: 'step_index', type: 'integer' },
      message: { to: 'message', type: 'string' },
      is_first: { to: 'is_first', type: 'boolean' },
      input_method: { to: 'input_method', type: 'string' },
      transcript_length: { to: 'transcript_length', type: 'integer' },
      placeholder_example_key: { to: 'placeholder_example_key', type: 'string', nullable: true },
      submit_method: { to: 'submit_method', type: 'string', nullable: true },
      message_length: { to: 'message_length', type: 'integer', nullable: true },
    },
    description: 'Home-tutor bar/sheet message submitted. placeholder_example_key/submit_method/message_length are home-bar-only (v1.3); absent from the lesson-entry-point path. On the lesson entry points a voice turn is logged when sent, like a text one, since US-1391 (before, a voice followup was logged only once Bao had answered).',
  },
  baohelp_response_received: {
    event: 'baohelp_response_viewed',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      entry_point: { to: 'entry_point', type: 'string' },
      control_feat_variant: { to: 'control_feat_variant', type: 'string', nullable: true },
      chapter_kind: { to: 'chapter_kind', type: 'string', nullable: true },
      step_id: { to: 'step_id', type: 'string', nullable: true },
      practice_kind: { to: 'practice_kind', type: 'string' },
      practice_part: { to: 'practice_part', type: 'string' },
      step_index: { to: 'step_index', type: 'integer' },
      response: { to: 'response', type: 'string' },
      input_method: { to: 'input_method', type: 'string' },
      error_kind: { to: 'error_kind', type: 'string', nullable: true },
      goal_reached: { to: 'is_goal_reached', type: 'boolean', nullable: true },
      suggestion_count: { to: 'suggestion_count', type: 'integer' },
      lesson_suggestion_count: { to: 'lesson_suggestion_count', type: 'integer' },
      is_sheet_open: { to: 'is_sheet_open', type: 'boolean', nullable: true },
    },
    description: "Bao tutor response shown to the child. On entry_point home this includes failures, where response is the error bubble and error_kind is set; the lesson entry points emit it on success only, so filter error_kind is null before comparing arms. is_goal_reached and suggestion_count come from both sheets, on successful replies only (a failed turn carries neither): is_goal_reached is Bao's own signal, null on a reply from an engine that predates it, and suggestion_count is how many chips the reply offered. lesson_suggestion_count is the subset of those chips naming one of the child's lessons; it is home-only (the lesson sheet's chips can only name a part of the lesson already open) and zero on an engine that predates refs. On the lesson entry points is_sheet_open is false when the reply landed while the sheet was closed: it shows when the child reopens the sheet on that anchor.",
  },
  baohelp_focus_updated: {
    event: 'baohelp_focus_updated',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      entry_point: { to: 'entry_point', type: 'string' },
      control_feat_variant: { to: 'control_feat_variant', type: 'string', nullable: true },
      chapter_kind: { to: 'chapter_kind', type: 'string', nullable: true },
      step_id: { to: 'step_id', type: 'string', nullable: true },
      practice_kind: { to: 'practice_kind', type: 'string' },
      practice_part: { to: 'practice_part', type: 'string' },
      step_index: { to: 'step_index', type: 'integer' },
      from_lesson_id: { to: 'from_lesson_id', type: 'string', nullable: true },
      to_lesson_id: { to: 'to_lesson_id', type: 'string', nullable: true },
      focus_source: { to: 'focus_source', type: 'string' },
    },
    description: "The lesson the home conversation is about changed, once per move: from_lesson_id is the lesson left (null on the first one) and to_lesson_id the lesson taken up (null when Bao drops back to a general answer). focus_source is chip when the child tapped a chip naming that lesson and inferred when Bao picked it from the conversation. Home-only — the lesson sheet is pinned to one lesson and never emits it — so lesson_id is always null here and the lesson in play is to_lesson_id.",
  },
  baohelp_focus_opened: {
    event: 'baohelp_focus_opened',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      entry_point: { to: 'entry_point', type: 'string' },
      control_feat_variant: { to: 'control_feat_variant', type: 'string', nullable: true },
      chapter_kind: { to: 'chapter_kind', type: 'string', nullable: true },
      step_id: { to: 'step_id', type: 'string', nullable: true },
      practice_kind: { to: 'practice_kind', type: 'string' },
      practice_part: { to: 'practice_part', type: 'string' },
      step_index: { to: 'step_index', type: 'integer' },
      message_count: { to: 'message_count', type: 'integer', nullable: true },
    },
    description: "The child tapped the focus pill and confirmed leaving the conversation for that lesson's page. lesson_id names the lesson opened: on entry_point home it is the one baohelp event where lesson_id is set (the same lesson as the last baohelp_focus_updated's to_lesson_id); on the lesson entry points (a Lesson-profile conversation) it is the anchored lesson, and entry_point is the lesson sheet's own. message_count is how many messages the child had sent when they left, so a drop-off can be told from a hand-off. Fires on the yes only; the confirmation being shown, and a no, emit nothing. The conversation is not resumed afterwards.",
  },
  baohelp_app_link_clicked: {
    event: 'baohelp_app_link_clicked',
    properties: {
      destination: { to: 'destination', type: 'string' },
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      entry_point: { to: 'entry_point', type: 'string' },
      control_feat_variant: { to: 'control_feat_variant', type: 'string', nullable: true },
      chapter_kind: { to: 'chapter_kind', type: 'string', nullable: true },
      step_id: { to: 'step_id', type: 'string', nullable: true },
      practice_kind: { to: 'practice_kind', type: 'string' },
      practice_part: { to: 'practice_part', type: 'string' },
      step_index: { to: 'step_index', type: 'integer' },
      message_count: { to: 'message_count', type: 'integer', nullable: true },
    },
    description: "The child tapped the App destination button Bao's latest home reply carried, pinned above the message box. destination is the engine's App destination id (cards_collection, cards_shop, missions, badges, streak, star_road, lessons, avatar, friends, referral, timetable); only ids this build knows ever show a button. Home-only, so lesson_id is always null. Fires on the tap, before the leave-chat confirmation: a following no still counts as interest in that screen, and a yes is the baohelp_closed that follows. message_count is how many messages the child had sent at the tap.",
  },
  baohelp_closed: {
    event: 'baohelp_closed',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      entry_point: { to: 'entry_point', type: 'string' },
      control_feat_variant: { to: 'control_feat_variant', type: 'string', nullable: true },
      chapter_kind: { to: 'chapter_kind', type: 'string', nullable: true },
      step_id: { to: 'step_id', type: 'string', nullable: true },
      practice_kind: { to: 'practice_kind', type: 'string' },
      practice_part: { to: 'practice_part', type: 'string' },
      step_index: { to: 'step_index', type: 'integer' },
      message_count: { to: 'message_count', type: 'integer', nullable: true },
      duration_ms: { to: 'duration_ms', type: 'integer' },
    },
    description: 'Bao tutor sheet closed. message_count and duration_ms are home-bar-only session stats, set together on that path and both absent on the lesson entry points; duration_ms cannot be declared nullable here because the registry pins one nullability per property name and four other events always send it.',
  },
  baohelp_history_opened: {
    event: 'baohelp_history_opened',
    properties: {
      entry_point: { to: 'entry_point', type: 'string' },
      history_source: { to: 'history_source', type: 'string' },
    },
    description: "The child's history of Bao conversations opened. entry_point is the chat it was opened from (home, smartbit, chapter_end, quiz_wrong, history for a reopened conversation); history_source is header (the history button of the chat), home_bar (the history button next to the home tutor bar, entry_point home) or saved_sheet (the one-time 'conversation saved' sheet shown on the first close).",
  },
  baohelp_history_conversation_clicked: {
    event: 'baohelp_history_conversation_clicked',
    properties: {
      entry_point: { to: 'entry_point', type: 'string' },
      conversation_kind: { to: 'conversation_kind', type: 'string' },
      history_section: { to: 'history_section', type: 'string' },
      conversation_position: { to: 'conversation_position', type: 'integer' },
    },
    description: 'A past conversation tapped in the history, which reopens it. conversation_kind is its stored Tutor profile: home or lesson; history_section is today, yesterday, thisWeek or older, and conversation_position its zero-based rank in that section.',
  },
  baohelp_history_new_conversation_clicked: {
    event: 'baohelp_history_new_conversation_clicked',
    properties: {
      entry_point: { to: 'entry_point', type: 'string' },
    },
    description: "The history's 'new conversation' button tapped: it closes the chat and goes to the child home with a blank Home tutor chat open, logged as baohelp_opened with entry_point history_new; the conversation itself starts on the first message.",
  },
  baohelp_history_conversation_archive_clicked: {
    event: 'baohelp_history_conversation_archive_clicked',
    properties: {
      entry_point: { to: 'entry_point', type: 'string' },
      conversation_kind: { to: 'conversation_kind', type: 'string' },
    },
    description: 'A conversation archived from the history (swipe, then the archive action): it leaves the history and takes no more messages.',
  },
  baohelp_history_conversation_archive_failed: {
    event: 'baohelp_history_conversation_archive_failed',
    properties: {
      entry_point: { to: 'entry_point', type: 'string' },
      conversation_kind: { to: 'conversation_kind', type: 'string' },
      error_kind: { to: 'error_kind', type: 'string', nullable: true },
    },
    description: 'The archive request of a conversation from the history failed: the card comes back and an error toast shows. error_kind is unavailable on a 404, transient otherwise.',
  },
  baohelp_saved_sheet_viewed: {
    event: 'baohelp_saved_sheet_viewed',
    properties: {
      entry_point: { to: 'entry_point', type: 'string' },
    },
    description: "The one-time 'conversation saved' sheet shown the first time a child closes a Bao chat after exchanging with Bao. entry_point is the chat that was closed.",
  },
  block_intro_viewed: {
    event: 'block_intro_viewed',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      chapter_id: { to: 'chapter_id', type: 'string', nullable: true },
      block_type: { to: 'block_type', type: 'string', nullable: true },
    },
    description: 'Intro screen listing the goals of a block session, shown before its first passage.',
  },
  block_intro_started: {
    event: 'block_intro_started',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      chapter_id: { to: 'chapter_id', type: 'string', nullable: true },
      block_type: { to: 'block_type', type: 'string', nullable: true },
    },
    description: 'Child tapped the start button of the block session intro.',
  },
  block_understood_clicked: {
    event: 'block_understood_clicked',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      chapter_id: { to: 'chapter_id', type: 'string', nullable: true },
      block_type: { to: 'block_type', type: 'string', nullable: true },
      item_index: { to: 'item_index', type: 'integer' },
      is_last_item: { to: 'is_last_item', type: 'boolean' },
    },
    description: 'Child tapped the understood button under a block passage or smartbit; is_last_item is true when it completes the chapter.',
  },
  practice_intro_viewed: {
    event: 'practice_intro_viewed',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      step_id: { to: 'step_id', type: 'string', nullable: true },
      practice_kind: { to: 'practice_kind', type: 'string' },
    },
    description: 'Intro screen of a Practice step shown, before its block.',
  },
  practice_intro_started: {
    event: 'practice_intro_started',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      step_id: { to: 'step_id', type: 'string', nullable: true },
      practice_kind: { to: 'practice_kind', type: 'string' },
    },
    description: 'Child tapped the start button of a Practice step\'s intro.',
  },
  practice_situation_viewed: {
    event: 'practice_situation_viewed',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      step_id: { to: 'step_id', type: 'string', nullable: true },
      practice_kind: { to: 'practice_kind', type: 'string' },
      practice_screen: { to: 'practice_screen', type: 'string' },
    },
    description: 'One screen of a Practice step\'s block shown after its intro, once per screen: practice_screen rule (the rule alone), steps (the rule\'s steps, revealed one at a time) or example (the lesson\'s own or an invented one, its steps revealed one at a time). Screens without data are skipped. practice_kind: method | exercise (an exercise block has no rule or steps screen).',
  },
  practice_situation_understood_clicked: {
    event: 'practice_situation_understood_clicked',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      step_id: { to: 'step_id', type: 'string', nullable: true },
      practice_kind: { to: 'practice_kind', type: 'string' },
    },
    description: 'Child tapped the understood button on the last screen of a Practice step\'s block; it opens the step\'s quiz.',
  },
  practice_next_step_clicked: {
    event: 'practice_next_step_clicked',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      step_id: { to: 'step_id', type: 'string', nullable: true },
      practice_kind: { to: 'practice_kind', type: 'string' },
      practice_screen: { to: 'practice_screen', type: 'string' },
      step_index: { to: 'step_index', type: 'integer' },
    },
    description: 'Child tapped to reveal the next step on a Practice screen; step_index is the number of steps shown before the tap.',
  },
  practice_next_screen_clicked: {
    event: 'practice_next_screen_clicked',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      step_id: { to: 'step_id', type: 'string', nullable: true },
      practice_kind: { to: 'practice_kind', type: 'string' },
      practice_screen: { to: 'practice_screen', type: 'string' },
    },
    description: 'Child tapped to move from practice_screen to the next screen of a Practice step\'s block.',
  },
  practice_quit: {
    event: 'practice_quit',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      step_id: { to: 'step_id', type: 'string', nullable: true },
      practice_kind: { to: 'practice_kind', type: 'string' },
      practice_screen: { to: 'practice_screen', type: 'string' },
    },
    description: 'Child confirmed quitting a Practice step from its header; practice_screen is intro when quitting before the first screen.',
  },
  practice_term_opened: {
    event: 'practice_term_opened',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      step_id: { to: 'step_id', type: 'string', nullable: true },
      practice_kind: { to: 'practice_kind', type: 'string' },
      practice_screen: { to: 'practice_screen', type: 'string' },
    },
    description: 'Child tapped an underlined term on a Practice reading screen to open its definition.',
  },
  practice_method_opened: {
    event: 'practice_method_opened',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      step_id: { to: 'step_id', type: 'string', nullable: true },
      practice_kind: { to: 'practice_kind', type: 'string' },
      practice_screen: { to: 'practice_screen', type: 'string' },
    },
    description: 'Child opened the "review the method" sheet of a Practice step; practice_screen is example on the reading screen, quiz above a question.',
  },
  practice_answer_history_opened: {
    event: 'practice_answer_history_opened',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      quiz_id: { to: 'quiz_id', type: 'string' },
      exercise_index: { to: 'exercise_index', type: 'integer' },
      step_index: { to: 'step_index', type: 'integer' },
    },
    description: 'Child opened the answer history of a Practice statement; step_index is the step of the question on screen.',
  },
  practice_term_closed: {
    event: 'practice_term_closed',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      step_id: { to: 'step_id', type: 'string', nullable: true },
      practice_kind: { to: 'practice_kind', type: 'string' },
      practice_screen: { to: 'practice_screen', type: 'string' },
      dismiss_method: { to: 'dismiss_method', type: 'string' },
    },
    description: 'Child closed the definition of an underlined term on a Practice reading screen; dismiss_method is button for the close button, swipe for a drag down or a tap on the mask, ask_bao when the child asked Bao about the term.',
  },
  practice_method_closed: {
    event: 'practice_method_closed',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      step_id: { to: 'step_id', type: 'string', nullable: true },
      practice_kind: { to: 'practice_kind', type: 'string' },
      practice_screen: { to: 'practice_screen', type: 'string' },
      dismiss_method: { to: 'dismiss_method', type: 'string' },
    },
    description: 'Child closed the "review the method" sheet of a Practice step; practice_screen as on practice_method_opened, dismiss_method is button for the close button, swipe for a drag down or a tap on the mask.',
  },
  practice_answer_history_closed: {
    event: 'practice_answer_history_closed',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      quiz_id: { to: 'quiz_id', type: 'string' },
      exercise_index: { to: 'exercise_index', type: 'integer' },
      step_index: { to: 'step_index', type: 'integer' },
      dismiss_method: { to: 'dismiss_method', type: 'string' },
    },
    description: 'Child closed the answer history of a Practice statement; dismiss_method is button for the close button, swipe for a drag down or a tap on the mask.',
  },
  block_hearts_intro_opened: {
    event: 'block_hearts_intro_opened',
    properties: {
      hearts_remaining: { to: 'hearts_remaining', type: 'integer', nullable: true },
    },
    description: 'Child tapped the hearts in the block session header to open the hearts explanation.',
  },
  lesson_header_chip_clicked: {
    event: 'lesson_header_chip_clicked',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      chip_kind: { to: 'chip_kind', type: 'string' },
    },
    description: 'Child tapped a chip of the lesson header; chip_kind is evaluation_date or photos.',
  },
  block_term_tour_viewed: {
    event: 'block_term_tour_viewed',
    description: 'First-time hint shown over a block passage, telling the child the underlined terms are tappable.',
  },
  block_term_tour_closed: {
    event: 'block_term_tour_closed',
    properties: {
      dismiss_method: { to: 'dismiss_method', type: 'string' },
    },
    description: 'Child dismissed the underlined-term hint; dismiss_method is tap_term when the tap landed on a term, tap_elsewhere otherwise.',
  },
  block_passage_term_opened: {
    event: 'block_passage_term_opened',
    properties: {
      block_type: { to: 'block_type', type: 'string', nullable: true },
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
    },
    description: 'Child opened the definition of an underlined term in a block passage.',
  },
  flashcards_intro_started: { event: 'flashcard_intro_started' },
  flashcards_tour_started: { event: 'flashcard_tour_started' },
  flashcards_tour_completed: {
    event: 'flashcard_tour_finished',
    properties: {
      skipped: { to: 'is_skipped', type: 'boolean' },
    },
  },
  flashcards_card_flipped: {
    event: 'flashcard_interacted',
    properties: {
      position: { to: 'position', type: 'integer' },
    },
    inject: {
      gesture: { value: 'flip', type: 'string' },
    },
  },
  flashcards_card_swiped: {
    event: 'flashcard_interacted',
    properties: {
      outcome: { to: 'outcome', type: 'string' },
      method: { to: 'swipe_method', type: 'string', nullable: true },
      position: { to: 'position', type: 'integer' },
    },
    inject: {
      gesture: { value: 'swipe', type: 'string' },
    },
  },
  flashcards_undo: {
    event: 'flashcard_interacted',
    inject: {
      gesture: { value: 'undo', type: 'string' },
    },
  },
  flashcards_pass_completed: {
    event: 'flashcard_pass_finished',
    properties: {
      to_review: { to: 'to_review_count', type: 'integer' },
      already_knew: { to: 'already_knew_count', type: 'integer' },
      total: { to: 'total_count', type: 'integer' },
    },
  },
  flashcards_lets_go_clicked: { event: 'flashcard_lets_go_clicked' },
  flashcards_restarted: {
    event: 'flashcard_started',
    properties: {
      run_number: { to: 'run_number', type: 'integer', nullable: true },
    },
    inject: {
      is_restart: { value: true, type: 'boolean' },
    },
  },
  flashcards_quit_mid_pass: {
    event: 'flashcard_pass_quit',
    properties: {
      swiped_count: { to: 'swiped_count', type: 'integer' },
    },
  },
  summer_banner_clicked: { event: 'summer_banner_clicked' },
  easter_banner_clicked: {
    event: 'easter_banner_clicked',
    description:
      'The child tapped the Easter seasonal lessons card on the home page.',
  },
  seasonal_spotlight_clicked: {
    event: 'seasonal_spotlight_clicked',
    properties: {
      season: { to: 'season_type', type: 'string' },
    },
    description:
      'The child tapped a seasonal collection spotlight on the Cards tab.',
  },
  summer_hub_viewed: { event: 'summer_hub_viewed' },
  summer_hub_line_clicked: {
    event: 'summer_hub_line_clicked',
    properties: {
      line: { to: 'hub_line', type: 'string' },
      subject: { to: 'subject', type: 'string', nullable: true },
    },
    description: 'Child tapped a line on the summer holidays hub.',
  },
  summer_parkour_viewed: {
    event: 'summer_parkour_viewed',
    properties: {
      total_quizzes: { to: 'quizzes_count', type: 'integer' },
      subject: { to: 'subject', type: 'string', nullable: true },
    },
    description: 'Summer cahier de vacances parkour page viewed.',
  },
  voice_input_record_started: { event: 'voice_input_recording_started' },
  voice_input_recording_completed: { event: 'voice_input_recording_finished' },
  voice_input_paused: { event: 'voice_input_pause_clicked' },
  voice_input_resumed: { event: 'voice_input_resume_clicked' },
  voice_input_playback_started: { event: 'voice_input_playback_started' },
  voice_input_deleted: { event: 'voice_input_deleted' },
  voice_input_submitted: { event: 'voice_input_submitted' },
  voice_input_transcribed: { event: 'voice_input_created' },
  voice_input_failed: { event: 'voice_input_failed' },
  voice_input_permission_denied: {
    event: 'voice_input_permission_resolved',
    inject: {
      permission_status: { value: 'denied', type: 'string' },
    },
  },
  voice_input_permission_settings_opened: { event: 'voice_input_permission_settings_opened' },
  voice_input_transcript_cleared: { event: 'voice_input_transcript_deleted' },
  camera_session_killed: {
    event: 'camera_session_failed',
    properties: {
      route: { to: 'route', type: 'string' },
      lessonId: { to: 'lesson_id', type: 'string', nullable: true },
      examId: { to: 'exam_id', type: 'string' },
      subject: { to: 'subject', type: 'string', nullable: true },
      source: { to: 'flow_source', type: 'string', nullable: true },
      platform: { to: 'platform', type: 'string', nullable: true },
      heapUsedMB: { to: 'heap_used_mb', type: 'float' },
      photosCount: { to: 'photos_count', type: 'integer' },
      ageSeconds: { to: 'age_seconds', type: 'float', nullable: true },
      jsBootMs: { to: 'js_boot_ms', type: 'float', nullable: true },
      // Non-nullable to match app_device_profiled (one nullability per property name).
      deviceModel: { to: 'device_model', type: 'string' },
      deviceOsVersion: { to: 'device_os_version', type: 'string' },
      appVersion: { to: 'app_version', type: 'string', nullable: true },
      control_feat_variant: { to: 'control_feat_variant', type: 'string', nullable: true },
      captureMode: { to: 'capture_mode', type: 'string', nullable: true },
      phase: { to: 'phase', type: 'string', nullable: true },
      sessionAgeMs: { to: 'session_age_ms', type: 'integer', nullable: true },
      lastVisibility: { to: 'last_visibility', type: 'string', nullable: true },
      hiddenForMs: { to: 'hidden_for_ms', type: 'integer', nullable: true },
    },
    inject: {
      failure_reason: { value: 'os_killed', type: 'string' },
    },
  },
  camera_activity_restored: {
    event: 'camera_session_recovered',
    properties: {
      methodName: { to: 'method_name', type: 'string' },
      success: { to: 'is_success', type: 'boolean' },
      hasData: { to: 'has_data', type: 'boolean', nullable: true },
      platform: { to: 'platform', type: 'string', nullable: true },
    },
    inject: {
      stage: { value: 'activity', type: 'string' },
    },
  },
  camera_kill_redirect: {
    event: 'camera_session_recovered',
    properties: {
      redirectPath: { to: 'redirect_path', type: 'string' },
      platform: { to: 'platform', type: 'string', nullable: true },
      readyMs: { to: 'ready_ms', type: 'float', nullable: true },
    },
    inject: {
      stage: { value: 'redirected', type: 'string' },
      is_success: { value: true, type: 'boolean' },
    },
    description:
      'The app redirected back to the pre-kill route. First-paint timing is app_boot_finished.duration_ms.',
  },
  camera_kill_photo_restored: {
    event: 'camera_session_recovered',
    properties: {
      examId: { to: 'exam_id', type: 'string' },
      photosCount: { to: 'photos_count', type: 'integer' },
      lessonId: { to: 'lesson_id', type: 'string', nullable: true },
    },
    inject: {
      stage: { value: 'photos', type: 'string' },
      is_success: { value: true, type: 'boolean' },
    },
  },
  camera_opened: {
    event: 'camera_session_started',
    properties: {
      flow_source: { to: 'flow_source', type: 'string', nullable: true },
      lessonId: { to: 'lesson_id', type: 'string', nullable: true },
      examId: { to: 'exam_id', type: 'string' },
      photosCount: { to: 'photos_count', type: 'integer' },
      heapUsedMB: { to: 'heap_used_mb', type: 'float' },
      control_feat_variant: { to: 'control_feat_variant', type: 'string', nullable: true },
    },
    description:
      'Right before the native camera or gallery opens, sent via sendBeacon so it survives an OS kill. Denominator for camera_session_failed.',
  },
  camera_result_restored: {
    event: 'camera_session_recovered',
    properties: {
      outcome: { to: 'outcome', type: 'string' },
      success: { to: 'is_success', type: 'boolean' },
      methodName: { to: 'method_name', type: 'string' },
      platform: { to: 'platform', type: 'string', nullable: true },
    },
    inject: { stage: { value: 'result', type: 'string' } },
    description:
      'What the OS handed back after a kill (photo | cancelled | error | none). A kill is an orphan AND outcome in (photo, none).',
  },
  camera_result_late: {
    event: 'camera_session_recovered',
    properties: {
      lateMs: { to: 'late_ms', type: 'float' },
      methodName: { to: 'method_name', type: 'string' },
      outcome: { to: 'outcome', type: 'string' },
      platform: { to: 'platform', type: 'string', nullable: true },
    },
    inject: { stage: { value: 'late', type: 'string' } },
    description:
      'A restored camera result that arrived after the fast-track stopped waiting; outcome is classified on the late result.',
  },
  camera_capture_finished: {
    event: 'camera_capture_finished',
    properties: {
      captureMethod: { to: 'capture_method', type: 'string' },
      // integer, not nullable: the registry pins one type per property name.
      durationMs: { to: 'duration_ms', type: 'integer' },
      width: { to: 'width', type: 'integer', nullable: true },
      height: { to: 'height', type: 'integer', nullable: true },
      fileSizeBytes: { to: 'file_size_bytes', type: 'integer', nullable: true },
      lessonId: { to: 'lesson_id', type: 'string', nullable: true },
      examId: { to: 'exam_id', type: 'string' },
      control_feat_variant: { to: 'control_feat_variant', type: 'string', nullable: true },
      // Only set on a native capture that ran because the web camera failed first.
      isFallback: { to: 'is_fallback', type: 'boolean', nullable: true },
      streamWidth: { to: 'stream_width', type: 'integer', nullable: true },
      streamHeight: { to: 'stream_height', type: 'integer', nullable: true },
      capsMaxWidth: { to: 'caps_max_width', type: 'integer', nullable: true },
      capsMaxHeight: { to: 'caps_max_height', type: 'integer', nullable: true },
      focusModes: { to: 'focus_modes', type: 'string', nullable: true },
      streamRequest: { to: 'stream_request', type: 'string', nullable: true },
      readyMs: { to: 'ready_ms', type: 'float', nullable: true },
      shutterMs: { to: 'shutter_ms', type: 'integer', nullable: true },
      convergeMs: { to: 'converge_ms', type: 'integer', nullable: true },
      encodeMs: { to: 'encode_ms', type: 'integer', nullable: true },
      encodeDrawMs: { to: 'encode_draw_ms', type: 'integer', nullable: true },
      encodeBlobMs: { to: 'encode_blob_ms', type: 'integer', nullable: true },
      scoreMs: { to: 'score_ms', type: 'float', nullable: true },
      stillMs: { to: 'still_ms', type: 'integer', nullable: true },
      framesPresented: { to: 'frames_presented', type: 'integer', nullable: true },
      framesScored: { to: 'frames_scored', type: 'integer', nullable: true },
      bestFrameIndex: { to: 'best_frame_index', type: 'integer', nullable: true },
      firstScore: { to: 'first_score', type: 'float', nullable: true },
      bestScore: { to: 'best_score', type: 'float', nullable: true },
      meanLuma: { to: 'mean_luma', type: 'float', nullable: true },
      focusTapCount: { to: 'focus_tap_count', type: 'integer', nullable: true },
      stillAttempted: { to: 'still_attempted', type: 'boolean', nullable: true },
      stillPolicy: { to: 'still_policy', type: 'string', nullable: true },
      stillWidth: { to: 'still_width', type: 'integer', nullable: true },
      stillHeight: { to: 'still_height', type: 'integer', nullable: true },
      stillScore: { to: 'still_score', type: 'float', nullable: true },
      stillSimilarity: { to: 'still_similarity', type: 'float', nullable: true },
      stillRejectedReason: { to: 'still_rejected_reason', type: 'string', nullable: true },
      viewWidth: { to: 'view_width', type: 'integer', nullable: true },
      viewHeight: { to: 'view_height', type: 'integer', nullable: true },
      previewFillPct: { to: 'preview_fill_pct', type: 'integer', nullable: true },
      frameKeptPct: { to: 'frame_kept_pct', type: 'integer', nullable: true },
      cameraProfile: { to: 'camera_profile', type: 'string', nullable: true },
      trackLabel: { to: 'track_label', type: 'string', nullable: true },
      backCameraCount: { to: 'back_camera_count', type: 'integer', nullable: true },
      lensSource: { to: 'lens_source', type: 'string', nullable: true },
      lensAttempts: { to: 'lens_attempts', type: 'integer', nullable: true },
      lensSwitchMs: { to: 'lens_switch_ms', type: 'integer', nullable: true },
      overconstrainedConstraint: { to: 'overconstrained_constraint', type: 'string', nullable: true },
      encodePath: { to: 'encode_path', type: 'string', nullable: true },
      drawCount: { to: 'draw_count', type: 'integer', nullable: true },
      drawMsMax: { to: 'draw_ms_max', type: 'integer', nullable: true },
      scorer: { to: 'scorer', type: 'string', nullable: true },
      scorerFallback: { to: 'scorer_fallback', type: 'boolean', nullable: true },
      hardwareConcurrency: { to: 'hardware_concurrency', type: 'integer', nullable: true },
    },
    description:
      'One capture completed (capture_method web_burst | web_takephoto | native). duration_ms runs from camera opened to photo back on every path; the stream, lens, timing, scoring, encode and still properties exist on web rows only.',
  },
  camera_capture_failed: {
    event: 'camera_capture_failed',
    properties: {
      captureMethod: { to: 'capture_method', type: 'string' },
      failureReason: { to: 'failure_reason', type: 'string' },
      control_feat_variant: { to: 'control_feat_variant', type: 'string', nullable: true },
      cameraProfile: { to: 'camera_profile', type: 'string', nullable: true },
      hardwareConcurrency: { to: 'hardware_concurrency', type: 'integer', nullable: true },
    },
    description: 'The web camera failed; the tap fell back to the native intent.',
  },
  progression_path_viewed: {
    event: 'progression_path_viewed',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      subject: { to: 'subject', type: 'string', nullable: true },
      current_step_kind: { to: 'current_step_kind', type: 'string', nullable: true },
      current_step_scope: { to: 'current_step_scope', type: 'string', nullable: true },
      current_step_index: { to: 'current_step_index', type: 'integer', nullable: true },
      steps_count: { to: 'steps_count', type: 'integer' },
      completed_steps_count: { to: 'completed_steps_count', type: 'integer' },
      is_completed: { to: 'is_completed', type: 'boolean' },
      ready_resource_kinds: { to: 'ready_resource_kinds', type: 'list' },
    },
    description: 'Child opened the A/B lesson progression path; captures which step is current, how far along they are and which resources were already ready.',
  },
  lesson_course_viewed: {
    event: 'lesson_course_viewed',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      subject: { to: 'subject', type: 'string', nullable: true },
      revision_sheets_count: { to: 'revision_sheets_count', type: 'integer' },
      is_grouping_pending: { to: 'is_grouping_pending', type: 'boolean' },
      has_lesson_chapters: { to: 'has_lesson_chapters', type: 'boolean' },
      has_lesson_digest: { to: 'has_lesson_digest', type: 'boolean' },
    },
    description: 'Child opened Mon cours, the list of a lesson\'s flashcards and Revision sheets.',
  },
  lesson_digest_viewed: {
    event: 'lesson_digest_viewed',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      viewer_role: { to: 'viewer_role', type: 'string' },
      entry_point: { to: 'entry_point', type: 'string' },
    },
    description:
      'Student or parent opened « Le résumé », the whole Lesson digest.',
  },
  lesson_digest_section_clicked: {
    event: 'lesson_digest_section_clicked',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      viewer_role: { to: 'viewer_role', type: 'string' },
      section_number: { to: 'section_number', type: 'integer' },
    },
    description:
      'Student or parent tapped a Sommaire row on « Le résumé » to jump to that part.',
  },
  lesson_digest_print_notify_requested: {
    event: 'lesson_digest_print_notify_requested',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      viewer_role: { to: 'viewer_role', type: 'string' },
    },
    description:
      "Student or parent tapped « C'est noté ! » on the « Le résumé » print panel with « Prévenez-moi quand l'impression sera disponible » still ticked.",
  },
  lesson_digest_print_opened: {
    event: 'lesson_digest_print_opened',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      viewer_role: { to: 'viewer_role', type: 'string' },
    },
    description:
      'Student or parent tapped the printer on « Le résumé », opening the print panel.',
  },
  lesson_digest_print_closed: {
    event: 'lesson_digest_print_closed',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      viewer_role: { to: 'viewer_role', type: 'string' },
      dismiss_method: { to: 'dismiss_method', type: 'string' },
      is_notify_requested: { to: 'is_notify_requested', type: 'boolean' },
    },
    description:
      "The « Le résumé » print panel closed. `dismiss_method` is `button` for « C'est noté ! », `swipe` for a drag down or a tap on the mask; `is_notify_requested` is true only when « C'est noté ! » was tapped with the box ticked.",
  },
  lesson_digest_closed: {
    event: 'lesson_digest_closed',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      viewer_role: { to: 'viewer_role', type: 'string' },
    },
    description:
      'Student or parent tapped the back arrow on « Le résumé », returning to Mon cours or to the parent lesson page.',
  },
  revision_sheet_clicked: {
    event: 'revision_sheet_clicked',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      subject: { to: 'subject', type: 'string', nullable: true },
      chapter_id: { to: 'chapter_id', type: 'string', nullable: true },
      chapter_kind: { to: 'chapter_kind', type: 'string', nullable: true },
      revision_sheet_position: { to: 'revision_sheet_position', type: 'integer' },
      revision_sheets_count: { to: 'revision_sheets_count', type: 'integer' },
    },
    description: 'Child tapped a Revision sheet card on Mon cours (a Lesson chapter or a KC group).',
  },
  progression_step_clicked: {
    event: 'progression_step_clicked',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      subject: { to: 'subject', type: 'string', nullable: true },
      step_kind: { to: 'step_kind', type: 'string' },
      step_scope: { to: 'step_scope', type: 'string' },
      step_index: { to: 'step_index', type: 'integer' },
      step_part_index: { to: 'step_part_index', type: 'integer', nullable: true },
      step_parts_count: { to: 'step_parts_count', type: 'integer', nullable: true },
      step_status: { to: 'step_status', type: 'string' },
    },
    description: 'Child tapped a step coin on the progression path, opening its info popover.',
  },
  progression_step_started: {
    event: 'progression_step_started',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      subject: { to: 'subject', type: 'string', nullable: true },
      step_kind: { to: 'step_kind', type: 'string' },
      step_scope: { to: 'step_scope', type: 'string' },
      step_index: { to: 'step_index', type: 'integer' },
      step_part_index: { to: 'step_part_index', type: 'integer', nullable: true },
      step_parts_count: { to: 'step_parts_count', type: 'integer', nullable: true },
      start_source: { to: 'start_source', type: 'string' },
    },
    description: 'Child launched a step\'s content from the progression path, either from the popover or the bottom primary CTA.',
  },
  progression_challenge_clicked: {
    event: 'progression_challenge_clicked',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      subject: { to: 'subject', type: 'string', nullable: true },
    },
    description: 'Child tapped Défier (parent challenge) from the progression path action bar.',
  },
  progression_course_clicked: {
    event: 'progression_course_clicked',
    properties: {
      lesson_id: { to: 'lesson_id', type: 'string', nullable: true },
      subject: { to: 'subject', type: 'string', nullable: true },
    },
    description: 'Child tapped Le cours (course summary page) from the progression path action bar.',
  },
  streak_counter_clicked: {
    event: 'streak_counter_clicked',
    properties: {
      current_streak: { to: 'current_streak', type: 'integer' },
      streak_source: { to: 'streak_source', type: 'string' },
    },
    description: 'Child tapped a flame counter, leading to the streak page. `streak_source` is `header` (home and profile headers) or `star_road`.',
  },
  streak_day_created: {
    event: 'streak_day_created',
    properties: {
      current_streak: { to: 'current_streak', type: 'integer' },
    },
    description: 'A finished quiz recorded a day the streak engine counts; `current_streak` is the run it grew to. The engagement event the whole feature is built to move.',
  },
  streak_page_viewed: {
    event: 'streak_page_viewed',
    properties: {
      current_streak: { to: 'current_streak', type: 'integer' },
      record_streak: { to: 'record_streak', type: 'integer' },
      streak_state: { to: 'streak_state', type: 'string' },
    },
    description: 'Child opened the streak page; captures the run they are on, their all-time record, and whether the server holds that run as ACTIVE, LOST or NONE.',
  },
  streak_month_selected: {
    event: 'streak_month_selected',
    properties: {
      direction: { to: 'direction', type: 'string' },
      month: { to: 'month', type: 'string' },
    },
    description: 'Child moved the streak calendar to another month; `month` is the month landed on (YYYY-MM).',
  },
  streak_shared: {
    event: 'streak_shared',
    properties: {
      current_streak: { to: 'current_streak', type: 'integer' },
      target: { to: 'target', type: 'string' },
    },
    description: 'Child shared their streak card from the streak page; `target` is the action they picked in the share panel (whatsapp/download/native).',
  },
  streak_celebration_viewed: {
    event: 'streak_celebration_viewed',
    properties: {
      current_streak: { to: 'current_streak', type: 'integer' },
      celebration_variant: { to: 'celebration_variant', type: 'string' },
    },
    description: 'Full-screen streak reward shown after a check-in recorded a new day: `start` (first day of a run), `milestone` (25/50/100) or `goal` (the chosen goal landed).',
  },
  streak_goal_viewed: {
    event: 'streak_goal_viewed',
    properties: {
      current_streak: { to: 'current_streak', type: 'integer' },
    },
    description: 'Goal-choice screen shown; the denominator streak_goal_selected was missing.',
  },
  streak_goal_selected: {
    event: 'streak_goal_selected',
    properties: {
      goal_days: { to: 'goal_days', type: 'integer' },
    },
    description: 'Child committed to a streak goal (5, 10 or 15 days) and the server took it.',
  },
  streak_goal_failed: {
    event: 'streak_goal_failed',
    properties: {
      goal_days: { to: 'goal_days', type: 'integer' },
      error_type: { to: 'error_type', type: 'string' },
    },
    description: 'The server refused a goal commit; `error_type` is its structured code, or `unknown` for a 500, a timeout or an offline POST.',
  },
  streak_celebration_closed: {
    event: 'streak_celebration_closed',
    properties: {
      current_streak: { to: 'current_streak', type: 'integer' },
      celebration_variant: { to: 'celebration_variant', type: 'string' },
    },
    description: 'Child dismissed the full-screen streak reward; pairs with streak_celebration_viewed to read drop-off per variant.',
  },
  celebration_viewed: {
    event: 'celebration_viewed',
    properties: {
      celebration_name: { to: 'celebration_name', type: 'string' },
    },
    description: 'A full-screen celebration (CelebrationModal) was shown; celebration_name says which one.',
  },
  celebration_closed: {
    event: 'celebration_closed',
    properties: {
      celebration_name: { to: 'celebration_name', type: 'string' },
      dismiss_method: { to: 'dismiss_method', type: 'string' },
    },
    description: 'A celebration was closed from its own controls: dismiss_method is close_button, continue_button or backdrop. Custom footer buttons log their own events.',
  },
  streak_saved: {
    event: 'streak_saved',
    properties: {
      days_covered: { to: 'days_covered', type: 'integer' },
      freezes_left: { to: 'freezes_left', type: 'integer' },
      current_streak: { to: 'current_streak', type: 'integer' },
    },
    description:
      'The app-open check-in reported that freezes covered missed days; `days_covered` freezes were spent, `current_streak` is the run that survived.',
  },
  streak_goal_claimed: {
    event: 'streak_goal_claimed',
    properties: {
      goal_days: { to: 'goal_days', type: 'integer' },
      reward_babz: { to: 'reward_babz', type: 'integer' },
    },
    description: 'Child tapped "Récupérer mes babz" and the server credited the streak-goal reward.',
  },
  streak_freeze_purchased: {
    event: 'streak_freeze_purchased',
    properties: {
      quantity: { to: 'quantity', type: 'integer' },
      price: { to: 'price', type: 'integer' },
      held_after: { to: 'held_after', type: 'integer' },
      purchase_source: { to: 'purchase_source', type: 'string' },
    },
    description: 'Child bought streak freezes with babz in one tap; `price` is the unit price paid, `held_after` the freezes held afterwards, `purchase_source` where they tapped: `streak_page` (the freeze row) or `streak_saved` (the panel telling them a freeze saved their streak).',
  },
  streak_protected_viewed: {
    event: 'streak_protected_viewed',
    properties: {
      days_covered: { to: 'days_covered', type: 'integer' },
      freezes_left: { to: 'freezes_left', type: 'integer' },
    },
    description: 'The child was actually shown the panel saying a freeze saved their streak. Distinct from `streak_saved`, which fires when the server spent the freeze even if the child was mid-quiz and the message was held back.',
  },
  streak_freeze_starter_granted: {
    event: 'streak_freeze_starter_granted',
    properties: {},
    description: 'The free starter freeze was granted to this child (fires once per child; replays are not captured).',
  },
  star_granted: {
    event: 'star_granted',
    properties: {
      star_source: { to: 'star_source', type: 'string' },
      star_amount: { to: 'star_amount', type: 'integer' },
      quizId: { to: 'quiz_id', type: 'string' },
      stars_total: { to: 'stars_total', type: 'integer' },
    },
    description: "A finished run credited stars on the star road. `star_amount` is the delta actually banked, not the run's raw score: a replay that beat an earlier best credits only the difference, and a replay that beat nothing fires nothing at all.",
  },
  star_road_tier_granted: {
    event: 'star_road_tier_granted',
    properties: {
      road_tier: { to: 'road_tier', type: 'integer' },
      stars_total: { to: 'stars_total', type: 'integer' },
    },
    description: 'The child crossed a star road tier. Fires on the crossing, not on the claim — the PRD reads median tier at D30 off this, and a child who never taps collect has still reached the tier.',
  },
  star_road_reward_claimed: {
    event: 'star_road_reward_claimed',
    properties: {
      claim_count: { to: 'claim_count', type: 'integer' },
      road_tier: { to: 'road_tier', type: 'integer' },
      claim_source: { to: 'claim_source', type: 'string' },
    },
    description: 'The child collected their star road rewards. One event per claim, not per reward: `claim_count` says how many tiers were bundled into it and `road_tier` is the highest one collected. `claim_source` names the surface that opened the reward screen (`road` or `quiz_result`); both are preceded by a `star_road_claim_opened` carrying the same `claim_source`.',
  },
  star_road_claim_opened: {
    event: 'star_road_claim_opened',
    properties: {
      claim_count: { to: 'claim_count', type: 'integer' },
      claim_source: { to: 'claim_source', type: 'string' },
    },
    description: 'The reward screen was opened, from the quiz result or from the road ("Tout collecter"). The gap between this and `star_road_reward_claimed`, per `claim_source`, is the children who saw their rewards and left without taking them.',
  },
  star_road_help_opened: {
    event: 'star_road_help_opened',
    description: 'The child tapped the info button of the star road header, opening the two-step help sheet on how to earn stars.',
  },
  star_road_help_next_clicked: {
    event: 'star_road_help_next_clicked',
    description: 'The child tapped Next on the first step of the star road help, moving to the replay step.',
  },
  star_road_help_back_clicked: {
    event: 'star_road_help_back_clicked',
    description: 'The child tapped Back on the replay step of the star road help, returning to the first step.',
  },
  star_road_help_closed: {
    event: 'star_road_help_closed',
    properties: {
      help_step: { to: 'help_step', type: 'string' },
      dismiss_method: { to: 'dismiss_method', type: 'string' },
    },
    description: 'The star road help sheet closed. `help_step` is the step on screen (`new_quiz` or `replay_quiz`); `dismiss_method` is `button` for the final call to action, `swipe` for a drag down or a tap on the mask.',
  },
  currency_help_closed: {
    event: 'currency_help_closed',
    properties: {
      wallet_currency: { to: 'wallet_currency', type: 'string' },
      help_source: { to: 'help_source', type: 'string' },
      dismiss_method: { to: 'dismiss_method', type: 'string' },
    },
    description: 'The currency help sheet closed. Same `wallet_currency` and `help_source` as its `currency_help_opened`; `dismiss_method` is `button` for Got it, `swipe` for a drag down or a tap on the mask.',
  },
  lesson_card_options_opened: {
    event: 'lesson_card_options_opened',
    properties: {
      lessonId: { to: 'lesson_id', type: 'string', nullable: true },
      trigger: { to: 'trigger', type: 'string' },
    },
    description: 'The child opened the options sheet of a lesson card on the home. `trigger` is `long_press` for a hold on the card, `more_button` for its ⋯ button.',
  },
  lesson_card_option_clicked: {
    event: 'lesson_card_option_clicked',
    properties: {
      lessonId: { to: 'lesson_id', type: 'string', nullable: true },
      buttonName: { to: 'button_name', type: 'string', nullable: true },
    },
    description: 'The child tapped an entry of a home lesson card options sheet. `button_name` is `view_photos`, `add_content`, `evaluation_date`, `rename` or `delete`.',
  },
  lesson_card_options_closed: {
    event: 'lesson_card_options_closed',
    properties: {
      lessonId: { to: 'lesson_id', type: 'string', nullable: true },
      dismiss_method: { to: 'dismiss_method', type: 'string' },
    },
    description: 'The home lesson card options sheet closed without picking an entry; `dismiss_method` is `swipe` for a drag down or a tap on the mask.',
  },
  parent_lesson_options_opened: {
    event: 'parent_lesson_options_opened',
    properties: {
      lessonId: { to: 'lesson_id', type: 'string', nullable: true },
    },
    description: 'The parent tapped the ⋯ button of the parent lesson page, opening its options sheet.',
  },
  parent_lesson_option_clicked: {
    event: 'parent_lesson_option_clicked',
    properties: {
      lessonId: { to: 'lesson_id', type: 'string', nullable: true },
      buttonName: { to: 'button_name', type: 'string', nullable: true },
    },
    description: 'The parent tapped an entry of the parent lesson page options sheet. `button_name` is `view_photos`, `add_content`, `evaluation_date`, `rename` or `delete`.',
  },
  parent_lesson_options_closed: {
    event: 'parent_lesson_options_closed',
    properties: {
      lessonId: { to: 'lesson_id', type: 'string', nullable: true },
      dismiss_method: { to: 'dismiss_method', type: 'string' },
    },
    description: 'The parent lesson page options sheet closed without picking an entry; `dismiss_method` is `swipe` for a drag down or a tap on the mask.',
  },
  currency_help_opened: {
    event: 'currency_help_opened',
    properties: {
      wallet_currency: { to: 'wallet_currency', type: 'string' },
      help_source: { to: 'help_source', type: 'string' },
    },
    description: 'The child tapped a wallet counter (Babz, Blizz or Blingz), opening the sheet that explains what the currency buys. `wallet_currency` is BABZ, BLIZZ or BLINGZ; `help_source` is `header` (home, profile), `avatar`, `shop` or `star_road`.',
  },
  star_road_opened: {
    event: 'star_road_opened',
    properties: {
      road_source: { to: 'road_source', type: 'string' },
    },
    description: 'The star road screen was opened, with `road_source` naming what led there: `home` for the Accueil block, `direct` for a cold start or a deep link. The quiz result opens the claim tunnel in place, so it never leads here.',
  },
  star_road_finished: {
    event: 'star_road_finished',
    properties: {
      stars_total: { to: 'stars_total', type: 'integer' },
    },
    description: 'The child reached the final tier. The road then freezes until season 2 while stars keep accruing, so this fires once.',
  },
  badge_list_viewed: {
    event: 'badge_list_viewed',
    properties: {
      unseen_count: { to: 'unseen_count', type: 'integer' },
    },
    description: 'Child opened the badge list page; captures how many badges were unseen at that moment.',
  },
  badge_showcase_viewed: {
    event: 'badge_showcase_viewed',
    properties: {
      filled_slots: { to: 'filled_slots', type: 'integer' },
      source: { to: 'showcase_source', type: 'string' },
    },
    description: 'Child opened the badge showcase-editing page; captures how many of the 3 slots were already filled and where the child came from (profile strip, or `direct` for a reload/deep link).',
  },
  badge_friend_showcase_viewed: {
    event: 'badge_friend_showcase_viewed',
    properties: {
      showcased_count: { to: 'showcased_count', type: 'integer' },
      profile_user_uuid: { to: 'profile_user_uuid', type: 'string' },
    },
    description: 'A non-empty badge showcase strip rendered on a friend\'s profile panel.',
  },
  friends_page_viewed: {
    event: 'friends_page_viewed',
    properties: {
      friends_count: { to: 'friends_count', type: 'integer' },
      invitations_count: { to: 'invitations_count', type: 'integer' },
      showcased_badges_count: { to: 'showcased_badges_count', type: 'integer' },
    },
    description: 'Child opened the Friends tab; counts the mates loaded when the tab settled (confirmed friends, pending invitations) and how many showcased badges were on display across them -- 0 when the badges flag is off. Usually the first page of 50, more when the persisted cache carries several (US-1337).',
  },
  friends_add_friend_clicked: {
    event: 'friends_add_friend_clicked',
    description: 'Child tapped the "add friends" call to action at the top of the Friends tab.',
  },
  friend_request_resolved: {
    event: 'friend_request_resolved',
    properties: {
      friend_request_answer: { to: 'friend_request_answer', type: 'string' },
      friend_request_source: { to: 'friend_request_source', type: 'string' },
    },
    description: 'Child accepted or declined a pending friend invitation, from the Friends tab or the inviter\'s profile.',
  },
  friend_profile_opened: {
    event: 'friend_profile_opened',
    properties: {
      profile_user_uuid: { to: 'profile_user_uuid', type: 'string' },
      profile_open_source: { to: 'profile_open_source', type: 'string' },
      mate_status: { to: 'mate_status', type: 'string' },
      showcased_count: { to: 'showcased_count', type: 'integer' },
    },
    description: 'Child opened a mate\'s profile from the Friends tab, either via the card or its "see profile" button; carries how many badges that mate showcases and whether they are a confirmed friend (`mate_status`) -- an invitation card opens a profile too.',
  },
  profile_picker_viewed: {
    event: 'profile_picker_viewed',
    properties: {
      profile_picker_source: { to: 'profile_picker_source', type: 'string' },
      profiles_count: { to: 'profiles_count', type: 'integer' },
      greeting_index: { to: 'greeting_index', type: 'integer', nullable: true },
    },
    description: 'The family profile picker showed: profile_picker_source is app_launch (shared phone, each cold start), profile_unknown_on_device (a parent session on a device last used by another profile), profile_menu, parent_lesson or family_code. profiles_count counts the family profiles shown; greeting_index is the random Bao greeting (the two full-screen sources only, null elsewhere). Fires once per opening.',
  },
  profile_picker_profile_selected: {
    event: 'profile_picker_profile_selected',
    properties: {
      profile_picker_source: { to: 'profile_picker_source', type: 'string' },
      profiles_count: { to: 'profiles_count', type: 'integer' },
      greeting_index: { to: 'greeting_index', type: 'integer', nullable: true },
      selected_user_type: { to: 'selected_user_type', type: 'string' },
      is_current_profile: { to: 'is_current_profile', type: 'boolean' },
    },
    description: 'A profile tapped in the family profile picker; selected_user_type is parent or child, is_current_profile tells whether it is the profile already signed in. Fires on the tap, before the session switch.',
  },
  profile_picker_add_clicked: {
    event: 'profile_picker_add_clicked',
    properties: {
      profile_picker_source: { to: 'profile_picker_source', type: 'string' },
      profiles_count: { to: 'profiles_count', type: 'integer' },
      greeting_index: { to: 'greeting_index', type: 'integer', nullable: true },
    },
    description: 'The add-a-profile tile tapped in the family profile picker.',
  },
  profile_switch_failed: {
    event: 'profile_switch_failed',
    properties: {
      profile_picker_source: { to: 'profile_picker_source', type: 'string' },
      profiles_count: { to: 'profiles_count', type: 'integer' },
      greeting_index: { to: 'greeting_index', type: 'integer', nullable: true },
    },
    description: 'A profile switch (or the switch to the parent before adding a profile) failed from the family profile picker; the tiles are re-enabled.',
  },
  badge_detail_opened: {
    event: 'badge_detail_opened',
    properties: {
      badge_code: { to: 'badge_code', type: 'string' },
      badge_family: { to: 'badge_family', type: 'string' },
      viewed_tier: { to: 'viewed_tier', type: 'string', nullable: true },
      is_unlocked: { to: 'is_unlocked', type: 'boolean' },
      detail_context: { to: 'detail_context', type: 'string' },
    },
    description: 'The badge detail bottom sheet opened, either from a tile tap (own) or a friend\'s showcase strip (friend_profile).',
  },
  badge_tier_selected: {
    event: 'badge_tier_selected',
    properties: {
      badge_code: { to: 'badge_code', type: 'string' },
      from_tier: { to: 'from_tier', type: 'string' },
      to_tier: { to: 'to_tier', type: 'string' },
    },
    description: 'Child picked a different owned tier in the badge detail sheet\'s tier switcher.',
  },
  badge_celebration_viewed: {
    event: 'badge_celebration_viewed',
    properties: {
      badge_code: { to: 'badge_code', type: 'string' },
      badge_tier: { to: 'badge_tier', type: 'string', nullable: true },
      badge_family: { to: 'badge_family', type: 'string' },
      celebration_trigger: { to: 'celebration_trigger', type: 'string' },
    },
    description: 'The full-screen badge-unlock celebration was shown, either from the in-app notification or the first tap on an unseen badge.',
  },
  badge_showcase_updated: {
    event: 'badge_showcase_updated',
    properties: {
      slots_count: { to: 'slots_count', type: 'integer' },
      badge_codes: { to: 'badge_codes', type: 'list' },
    },
    description: 'The child saved their badge showcase selection (successful PUT).',
  },
  badge_share_opened: {
    event: 'badge_share_opened',
    properties: {
      badge_code: { to: 'badge_code', type: 'string' },
      share_source: { to: 'share_source', type: 'string' },
    },
    description: 'The badge share sheet opened, from either the celebration modal or the detail sheet.',
  },
  badge_share_clicked: {
    event: 'badge_share_clicked',
    properties: {
      badge_code: { to: 'badge_code', type: 'string' },
      target: { to: 'target', type: 'string' },
    },
    description: 'Child tapped a share action (WhatsApp, download, or the native share sheet) in the badge share sheet.',
  },
  app_update_finished: {
    event: 'app_update_finished',
    properties: {
      outcome: { to: 'update_outcome', type: 'string' },
      floor: { to: 'floor', type: 'string', nullable: true },
      durationMs: { to: 'duration_ms', type: 'integer' },
      // Always present on this event; declared nullable because the registry enforces a single
      // nullability per property name and `app_update_failed` can genuinely omit it (the check can
      // throw before the running version is known).
      fromVersion: { to: 'from_version', type: 'string', nullable: true },
      toVersion: { to: 'to_version', type: 'string', nullable: true },
      bytesTotal: { to: 'bytes_total', type: 'integer', nullable: true },
      connectionType: { to: 'connection_type', type: 'string', nullable: true },
      updateMode: { to: 'update_mode', type: 'string', nullable: true },
      fallbackReason: { to: 'fallback_reason', type: 'string', nullable: true },
      filesTotal: { to: 'files_total', type: 'integer', nullable: true },
      filesMissing: { to: 'files_missing', type: 'integer', nullable: true },
      probeMs: { to: 'probe_ms', type: 'integer', nullable: true },
      bytesMissing: { to: 'bytes_missing', type: 'integer', nullable: true },
      downloadAttempts: { to: 'download_attempts', type: 'integer', nullable: true },
      attemptDurationMs: { to: 'attempt_duration_ms', type: 'integer', nullable: true },
      // Capgo's download+unzip pipeline progress, not a byte fraction: on iOS zip mode it stays 0 during the transfer and jumps to 70 once the zip has arrived.
      lastProgressPercent: { to: 'last_progress_percent', type: 'integer', nullable: true },
      batteryLevel: { to: 'battery_level', type: 'float', nullable: true },
      isCharging: { to: 'is_charging', type: 'boolean', nullable: true },
    },
    description: 'An OTA update check completed without error: found up to date, updated, or refused (below_floor / no_url or denied — all distinct from up_to_date). connection_type is wifi | cellular | none | unknown on iOS (from @capacitor/network, live during the gate, absent on shells without the plugin) and the Network Information effectiveType (4g, 3g, ...) on Android.',
  },
  app_update_failed: {
    event: 'app_update_failed',
    properties: {
      failureReason: { to: 'failure_reason', type: 'string' },
      durationMs: { to: 'duration_ms', type: 'integer' },
      // Absent when the check threw before it could read the running bundle's version, which is
      // precisely the case this event reports most often.
      fromVersion: { to: 'from_version', type: 'string', nullable: true },
      toVersion: { to: 'to_version', type: 'string', nullable: true },
      bytesTotal: { to: 'bytes_total', type: 'integer', nullable: true },
      connectionType: { to: 'connection_type', type: 'string', nullable: true },
      updateMode: { to: 'update_mode', type: 'string', nullable: true },
      fallbackReason: { to: 'fallback_reason', type: 'string', nullable: true },
      filesTotal: { to: 'files_total', type: 'integer', nullable: true },
      filesMissing: { to: 'files_missing', type: 'integer', nullable: true },
      probeMs: { to: 'probe_ms', type: 'integer', nullable: true },
      bytesMissing: { to: 'bytes_missing', type: 'integer', nullable: true },
      filesMissingAfter: { to: 'files_missing_after', type: 'integer', nullable: true },
      downloadAttempts: { to: 'download_attempts', type: 'integer', nullable: true },
      failureDetail: { to: 'failure_detail', type: 'string', nullable: true },
      attemptDurationMs: { to: 'attempt_duration_ms', type: 'integer', nullable: true },
      // Capgo's download+unzip pipeline progress, not a byte fraction: on iOS zip mode it stays 0 during the transfer and jumps to 70 once the zip has arrived.
      lastProgressPercent: { to: 'last_progress_percent', type: 'integer', nullable: true },
      batteryLevel: { to: 'battery_level', type: 'float', nullable: true },
      isCharging: { to: 'is_charging', type: 'boolean', nullable: true },
    },
    description: 'An OTA update attempt failed, either while checking for a new bundle or while downloading one. connection_type is wifi | cellular | none | unknown on iOS (from @capacitor/network, live during the gate, absent on shells without the plugin) and the Network Information effectiveType (4g, 3g, ...) on Android. `checksum_missing`: this shell\'s plugin requires a zip checksum the served row lacks, so it stays on its current bundle.',
  },
  native_update_required_viewed: {
    event: 'native_update_required_viewed',
    properties: {
      platform: { to: 'platform', type: 'string', nullable: true },
      fromVersion: { to: 'from_version', type: 'string', nullable: true },
    },
    description: 'The undismissable full-screen store-update panel rendered — the device is on a native build the backend will no longer serve OTA updates to. Without this, an affected device reports app_update_finished { outcome: up_to_date } and looks healthy.',
  },
  appsflyer_uid_resolved: {
    event: 'appsflyer_uid_resolved',
    properties: {
      isSuccess: { to: 'is_success', type: 'boolean' },
      durationMs: { to: 'duration_ms', type: 'integer' },
    },
    description: 'AppsFlyer startSDK() settled (resolved, timed out, or rejected) and we attempted to read the resolved UID. is_success is false when startSDK never settled within START_TIMEOUT_MS or the UID came back empty; makes install-attribution health observable for the first time (zero prior signal across 30 days of PostHog data).',
  },
  app_boot_recovered: {
    event: 'app_boot_recovered',
    properties: {
      durationMs: { to: 'duration_ms', type: 'integer' },
    },
    description: 'The boot-completion watchdog (BOOT_WATCHDOG_MS) fired: the post-boot-sequence tail (ATT prompt, Facebook tracking, AppsFlyer init) had not reached setIsReady(true) within the timeout, so the watchdog force-rendered the app instead. duration_ms is the elapsed time from arming to expiry (always ≥ BOOT_WATCHDOG_MS). This never reverts the bundle — a stalled third-party SDK is not a broken bundle — so a spike here is a hang to investigate, not a crash-loop signal.',
  },
  splash_released: {
    event: 'app_boot_finished',
    properties: {
      releaseReason: { to: 'release_reason', type: 'string' },
      durationMs: { to: 'duration_ms', type: 'integer' },
      bootPath: { to: 'boot_path', type: 'string' },
      reloadCoverShow: { to: 'reload_cover_show', type: 'string', nullable: true },
      reloadCoverSource: { to: 'reload_cover_source', type: 'string', nullable: true },
      reloadCoverBranch: { to: 'reload_cover_branch', type: 'string', nullable: true },
      reloadCoverAgeMs: { to: 'reload_cover_age_ms', type: 'integer', nullable: true },
    },
    description: 'The native splash was released: the real first-paint timing (Android WebView has no paint entries). release_reason: first_paint | watchdog | gate | fallback (the 20 s backstop, the slow path to watch). duration_ms is since webview navigation start. When this boot follows an app-initiated reload under the native reload cover, reload_cover_* describe that cover (absent = none preceded the boot, or the record was older than 10 min): reload_cover_show = shown | timed_out (show() missed its 500 ms bound) | rejected | skipped_under_hold (Android launch hold, expected) | pending; reload_cover_source = gate_set | gate_retry | error_page | environment_switch | preview; reload_cover_branch = capacitor | seasonal (shell <= 1607); reload_cover_age_ms = cover start to the new JS context starting, i.e. reload latency. Known limit: the record is consumed when the context loads, so a context that reloads again before its first release loses it (chained reloads under-count).',
  },
  reload_cover_failed: {
    event: 'reload_cover_failed',
    properties: {
      failureReason: { to: 'failure_reason', type: 'string' },
      durationMs: { to: 'duration_ms', type: 'integer' },
      reloadCoverShow: { to: 'reload_cover_show', type: 'string', nullable: true },
      reloadCoverSource: { to: 'reload_cover_source', type: 'string', nullable: true },
      reloadCoverBranch: { to: 'reload_cover_branch', type: 'string', nullable: true },
    },
    description: 'The native reload cover was lifted by the JS context that raised it, before its reload took effect (a reload that does take effect kills this context, and the next boot reports the cover through app_boot_finished reload_cover_*). failure_reason: operation_failed (the set()/reload() rejected; no reload will come, and this is how a gate set() failure surfaces) | fallback_guard (the 10 s guard) | gate_apply_grace (the update gate lifted after APPLY_GRACE_MS). For fallback_guard and gate_apply_grace the reload may still succeed afterwards: correlate with the next app_boot_finished (reload_cover_*) and duration_ms. gate_stall and gate_error only apply if a cover is still up when the gate stalls or fails (rare). duration_ms is cover start to lift. reload_cover_show/source/branch as on app_boot_finished.',
  },
  app_bundle_recovered: {
    event: 'app_bundle_recovered',
    properties: {
      failureReason: { to: 'failure_reason', type: 'string' },
      fromVersion: { to: 'from_version', type: 'string', nullable: true },
    },
    description: 'A Capgo bundle was reverted (failed_bundle / repeated_failures / render_failures — crashRecovery.ts) and the app has now completed a subsequent, healthy launch. Persisted at revert time and emitted here, one launch later, because the revert path tears down the webview and never settles on success, and the revert cohort is disproportionately offline — emitting at revert time would undercount the exact incident this signal exists to surface. Without this, a denylist/revert only ever produced a Sentry error string, with no dashboard signal.',
  },
  app_device_profiled: {
    event: 'app_device_profiled',
    properties: {
      deviceModel: { to: 'device_model', type: 'string' },
      deviceOsVersion: { to: 'device_os_version', type: 'string' },
      devicePlatform: { to: 'device_platform', type: 'string' },
    },
    description: 'Emitted once per launch from initAnalytics, right after Device.getInfo() resolves, carrying install_id (already a super-property by this point) alongside device class. Early-boot OTA events fire before initAnalytics resolves and so carry only install_id + the autocaptured $os; this event lets OTA telemetry be sliced by device model/OS version via an install_id join in HogQL, without hoisting Device.getInfo() into the boot-blocking path.',
  },
};
